import { useCallback, useEffect, useRef, useState } from "react";
import type { ChangeEvent, Dispatch, ReactElement, ReactNode, RefObject,
              SetStateAction } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { ApiClient, Angebot, AngebotVersion, Ansprechpartner, BelegAblage, Benutzer,
              PositionsStand, Unternehmen, Vorlagenauswahl } from "@kundeninfo/api-client";
import { Feld, Modal, ModalFuss, darfAbbrechen, useBeforeUnload,
         useFormularZustand, useBestaetigung } from "@kundeninfo/ui";
import { Auswahl, Formular, Hinweis, Tabelle } from "@kundeninfo/ui/me-design";
import type { FormularElement } from "@kundeninfo/ui/me-design";
import { Belegvorschau } from "./Belegvorschau.js";
import { PositionsEditor, PlatzhalterTextfeld,
         laufzeitVorschauWerte } from "./PositionsEditor.js";
import type { PositionsEditorClient } from "./PositionsEditor.js";
import { Aufklapper, Kopfblock, useKopffaltung } from "./Kopffaltung.js";
import type { Kopffaltung } from "./Kopffaltung.js";
import { BelegParteien, PARTEIEN_KOPFFELDER } from "./BelegParteien.js";
import type { BelegParteienModalClient } from "./BelegParteien.js";
import { Zahlplan, ZAHLPLAN_KOPFFELDER, istZahlplanFeld } from "./Zahlplan.js";
import type { ZahlplanModalClient } from "./Zahlplan.js";
import { Anhaenge, ANHAENGE_KOPFFELDER } from "./Anhaenge.js";
import type { AnhaengeClient } from "./Anhaenge.js";
import { formatBetrag, formatDatum } from "../format.js";
import { ANGEBOT_STATUS_LABEL } from "../vorgangsstatus.js";
import { apiMeldung, betragText, betragZahl, entwuerfeGleich,
         kopfBetragText, kopffeldAusFehler, kopffeldId, LAUFZEIT_KOPFFELDER,
         nettobetragLabel, oderNull,
         PDF_HINWEIS_ART, speichereBlob, speichereParteien, speichereZahlplan,
         useParteienGriff, useZahlplanGriff } from "./vorgangsdetail.js";
import type { KopffeldTabelle, PdfMeldung } from "./vorgangsdetail.js";
// Die Klassennamen bleiben `kang__` (Etappe 1, Aufgabe 2: keine Umbenennung an
// herausgelösten Bestandsteilen) — umgezogen ist in Fixrunde 1 nur die DATEI: Die Regeln
// dessen, was DIESER Baustein zeichnet, stehen jetzt hier statt im Stilblatt des Reiters.
// Damit bringt der Baustein seine Gestalt überallhin mit, ohne dass components/ nach views/
// hineingreifen müsste.
import "./AngebotDetail.css";

/**
 * Das ANGEBOTSDETAIL als eigener Baustein (Bereichs-Spalten, Etappe 1, Aufgabe 2).
 *
 * Herausgelöst aus views/KundeAngebote.tsx, damit der Akten-Reiter und der spätere
 * Angebots-BEREICH (Aufgabe 3) buchstäblich DASSELBE Detail zeichnen — nicht zwei Masken,
 * die sich anfangs gleichen und mit dem dritten Fix auseinanderlaufen. Der Umbau ist rein
 * strukturell: Es kommt kein Verhalten hinzu und es fällt keines weg.
 *
 * Was der Baustein trägt: die Kopfzeile des Vorgangs (Nummer, Kürzel, Titel, Status), die
 * Datenzeile (Betrag, Gültigkeit, Ansprechpartner), das Bearbeiten samt Positions-Editor
 * und Vorlagen-Anwenden, die Statusaktionen entlang der erlaubten Übergänge, „Als PDF"
 * mitsamt der `fields[]`-Befundliste und die Angebot→Auftrag-Naht.
 *
 * Was er NICHT weiss: dass es eine Kundenakte gibt. Die Liste, das Anlegen, der
 * Meldungsstreifen und die Frage, WELCHER Vorgang gerade bearbeitet wird, gehören dem
 * Rahmen — er reicht sie als Eigenschaften und Rückrufe herein (`bearbeitung`, `gesperrt`,
 * `onBearbeiten`/`onSchliessen`/`onNeuLaden`/`onHinweis`/`onFortschreiben`). So trägt der
 * Bereich in Aufgabe 3 denselben Baustein, ohne dass hier ein Zweig „im Bereich" entsteht;
 * und ein späterer Rechnungs-Bereich könnte es genauso.
 *
 * Der Vorgang kommt als GELADENER Satz herein (`angebot`), nicht als blosse Kennung: Der
 * Reiter führt seine Liste bereits, und ein Nachladen je Zeile wäre bei zwanzig Angeboten
 * zwanzig zusätzliche Abrufe — eine Verhaltensänderung, die dieser Umbau ausdrücklich nicht
 * machen darf. Der Bereich sucht seinen Satz in Aufgabe 3 genauso aus seiner eigenen Liste.
 */

/** Nur die Klientenmethoden, die das DETAIL braucht — Liste und Anlegen bleiben beim
 *  Rahmen (siehe `AngeboteClient` in views/KundeAngebote.tsx). */
export type AngebotDetailClient = Pick<ApiClient,
  "getOffer" | "updateOffer" | "setOfferStatus"
  | "neueOfferVersion" | "kopiereOffer" | "listOfferVersions"
  | "listContacts" | "listUsers" | "listPlaceholders" | "listTemplates" | "applyTemplate"
  | "renderOffer"
  // Belegvorschau (24.09.2026): die Seitenbilder und die PDF-Ablage in der Kundenakte.
  | "vorschauOffer" | "getOfferAblage" | "createOfferAblage">
  & PositionsEditorClient
  & BelegParteienModalClient
  & ZahlplanModalClient
  // Der Kasten „Anhänge" (Paket „Textbausteine als Anhang", 24.09.2026) — fünf Wege, von
  // denen das Angebot vier benutzt (`anhangSichtbarkeit` gibt es nur am Auftrag; der
  // Ausschnitt bleibt trotzdem gemeinsam, damit es nur EINEN Kasten gibt).
  & AnhaengeClient;

/**
 * Die AUSWAHLLISTEN, die Anlege- und Bearbeitungsmaske gemeinsam brauchen. Sie werden vom
 * RAHMEN geladen (`useAngebotsUmfeld`) und hier hereingereicht, nicht je Detail geholt:
 * Sonst zöge jede Listenzeile ihre eigenen Ansprechpartner, Benutzer, Platzhalter und
 * Vorlagen — vier Abrufe mal zwanzig Zeilen, für Daten, die für alle dieselben sind.
 */
export type AngebotsUmfeld = {
  kontakte: Ansprechpartner[];
  benutzer: Benutzer[];
  platzhalter: string[];
  vorlagen: Vorlagenauswahl[];
};

/**
 * Lädt die Auswahllisten einer Angebots-Ansicht: aktive Ansprechpartner des Kunden, aktive
 * Benutzer, die Platzhalternamen und die Vorlagen des betroffenen Unternehmens.
 *
 * Scheitert einer der Abrufe, bleibt die Ansicht bedienbar — die Felder sind optional, und
 * „ohne Vorlage" bleibt immer möglich. `vorlagenFirma` ist das Unternehmen, dessen Vorlagen
 * gerade gebraucht werden (leer = keine): Eine Vorlage gehört immer genau einer Firma, eine
 * gemeinsame Liste über alle wäre eine Auswahl, aus der die Hälfte 422 ergäbe.
 */
export function useAngebotsUmfeld(
  client: Pick<AngebotDetailClient,
    "listContacts" | "listUsers" | "listPlaceholders" | "listTemplates">,
  kundeId: string,
  vorlagenFirma: string,
): AngebotsUmfeld {
  const [kontakte, setKontakte] = useState<Ansprechpartner[]>([]);
  const [benutzer, setBenutzer] = useState<Benutzer[]>([]);
  const [platzhalter, setPlatzhalter] = useState<string[]>([]);
  const [vorlagen, setVorlagen] = useState<Vorlagenauswahl[]>([]);

  useEffect(() => {
    let ab = false;
    client.listContacts(kundeId)
      .then(({ items }) => { if (!ab) setKontakte(items.filter((k) => k.status === "ACTIVE")); })
      .catch(() => { /* optionales Feld — kein Aufhalten */ });
    return () => { ab = true; };
  }, [client, kundeId]);

  useEffect(() => {
    let ab = false;
    client.listUsers()
      .then(({ items }) => { if (!ab) setBenutzer(items.filter((b) => b.active)); })
      .catch(() => { /* optionales Feld — kein Aufhalten */ });
    return () => { ab = true; };
  }, [client]);

  useEffect(() => {
    let ab = false;
    client.listPlaceholders()
      .then(({ variablen }) => { if (!ab) setPlatzhalter(variablen); })
      .catch(() => { /* Auswahlhilfe — kein Aufhalten */ });
    return () => { ab = true; };
  }, [client]);

  useEffect(() => {
    if (vorlagenFirma === "") { setVorlagen([]); return; }
    let ab = false;
    client.listTemplates(vorlagenFirma)
      .then(({ items }) => { if (!ab) setVorlagen(items); })
      .catch(() => { /* Auswahlhilfe — kein Aufhalten; „ohne Vorlage" bleibt möglich */ });
    return () => { ab = true; };
  }, [client, vorlagenFirma]);

  return { kontakte, benutzer, platzhalter, vorlagen };
}

type OfferStatus = Angebot["status"];

/**
 * Die erlaubten Statusübergänge — bewusst hier NACHGEBILDET, nicht aus @kundeninfo/domain
 * importiert: web darf nur `ui` und `api-client` importieren (check-boundaries). Die Regel
 * ist die aus der Spec (offerUebergangErlaubt): `DRAFT → SENT`; `SENT → ACCEPTED | REJECTED`;
 * aus jedem Nicht-Endzustand `→ WITHDRAWN`. Endzustände (ACCEPTED/REJECTED/WITHDRAWN) bieten
 * nichts an. Der Server prüft denselben Übergang noch einmal (422), das hier ist die
 * Bedienführung, nicht die Wahrheit.
 */
function erlaubteFolgestatus(von: OfferStatus): OfferStatus[] {
  if (von === "DRAFT") return ["SENT", "WITHDRAWN"];
  if (von === "SENT") return ["ACCEPTED", "REJECTED", "WITHDRAWN"];
  // ACCEPTED, REJECTED, WITHDRAWN und SUPERSEDED sind Endzustände. SUPERSEDED wird
  // ausserdem NIE von Hand gesetzt — er entsteht allein durch „Neue Version".
  return [];
}

/** Der Verb-Knopftext je Zielstatus (Bedienführung: was der Klick TUT). */
const STATUS_AKTION: Record<OfferStatus, string> = {
  DRAFT: "Zurück in Entwurf",
  // NICHT „Senden": Der Knopf verschickt nichts, er setzt einen Status. Der alte Text
  // war ein Versprechen, das die Anwendung nicht einlöst (Spec, Abschnitt
  // „Oberfläche"); der Hinweis daneben sagt es noch einmal ausdrücklich.
  SENT: "Als gesendet markieren",
  ACCEPTED: "Annehmen",
  REJECTED: "Ablehnen",
  WITHDRAWN: "Zurückziehen",
  // Steht nur der Vollständigkeit halber in der Tabelle — SUPERSEDED taucht in
  // `erlaubteFolgestatus` nie auf und bekommt deshalb nie einen Knopf.
  SUPERSEDED: "Veraltet",
};

/** Der Satz neben „Als gesendet markieren" (Spec, wörtlich). */
const SENT_HINWEIS = "Setzt nur den Status. Es wird nichts versendet.";

/**
 * Der Versionszusatz — NACHGEBILDET wie `erlaubteFolgestatus`, weil `web` nur `ui` und
 * `api-client` einführen darf (check-boundaries). Die Elle ist `versionsZusatz` in
 * `@kundeninfo/domain`; dieselbe Regel, dieselben Worte: leer bei 1, sonst „v<n>".
 */
function versionsZusatz(versionNumber: number): string {
  return Number.isInteger(versionNumber) && versionNumber >= 2 ? `v${versionNumber}` : "";
}

/** Die deutschen Beschriftungen der Rückfragen. Sie stehen hier und nicht im Aufruf,
 *  damit der Wortlisten-Wächter sie findet und beide Sätze nebeneinander lesbar sind. */
const FRAGE_NEUE_VERSION =
  "Es entsteht eine neue Version dieses Angebots mit derselben Nummer. Die aktuelle "
  + "Fassung wird dabei zur alten Version und lässt sich danach nicht mehr ändern. "
  + "Fortfahren?";
const FRAGE_WIEDERHERSTELLEN =
  "Es entsteht eine neue Version mit den Inhalten DIESER älteren Fassung. Die aktuelle "
  + "Fassung wird dabei zur alten Version und lässt sich danach nicht mehr ändern. "
  + "Fortfahren?";
const FRAGE_KOPIEREN =
  "Es entsteht ein neues Angebot mit EIGENER Nummer und denselben Inhalten. Das "
  + "bisherige Angebot bleibt unverändert. Fortfahren?";

/** Die Formularwerte. Alle Angaben sind Zeichenketten, damit ein geleertes Feld erst beim
 *  Absenden zu null/0 wird. */
export type AngebotEntwurf = {
  companyId: string;
  /** Die beim ANLEGEN gewählte Vorlage („" = ohne Vorlage). Sie geht als `vorlageId` mit —
   *  Anlegen und Anwenden in EINEM Zug; am bestehenden Angebot hat das Anwenden einen
   *  eigenen Weg (siehe `vorlageAnwenden`). */
  vorlageId: string;
  title: string;
  netAmount: string;
  contactId: string;
  responsibleUserId: string;
  validUntil: string;
  note: string;
  /** Rohtexte MIT ihren `#{…}`-Tokens — gespeichert wird genau das, aufgelöst wird erst
   *  fürs PDF (die Vorschau darunter ist nur eine Lesehilfe). */
  introText: string;
  outroText: string;
  /**
   * Die Laufzeitfelder aus Migration 031. Die WERTE als Zeichenkette, damit ein geleertes
   * Feld beim Absenden zu `null` wird statt zu 0 — „keine Mindestlaufzeit" und „null
   * Monate" sind zwei verschiedene Aussagen.
   *
   * Die EINHEITEN behalten ihren Wert auch bei leerem Feld: Sonst würde eine einmal
   * gewählte Angabe „Jahre" durch ein Leeren des Zahlenfeldes still zu „Monate"
   * (dieselbe Überlegung wie bei `noticeReference` seit Migration 008). Vorher gab es hier
   * nur zwei nackte Monatszahlen (`minimumTermMonths`/`renewalPeriodMonths`) und keine
   * Vertragslaufzeit — Migration 031 macht daraus vier Paare aus Zahl und Einheit.
   */
  termValue: string;
  termUnit: "MONTHS" | "YEARS";
  minimumTermValue: string;
  minimumTermUnit: "MONTHS" | "YEARS";
  renewalPeriodValue: string;
  renewalPeriodUnit: "MONTHS" | "YEARS";
  noticePeriodValue: string;
  noticePeriodUnit: "MONTHS" | "YEARS";
  noticeReference: "TERM_END" | "CALENDAR_YEAR_END";
  /** Die Periodenregel des Zahlplans (Migration 026) — Kalender- oder Vertragsjahr. Sie
   *  steht am KOPF, weil sie für den ganzen Beleg gilt, nicht je Rate. */
  periodBasis: "CALENDAR_YEAR" | "CONTRACT_YEAR";
};

export function leererAngebotEntwurf(
  companies: Unternehmen[], arbeitsunternehmenId: string | null,
): AngebotEntwurf {
  return {
    // Das Arbeitsunternehmen (ME-Etappe 1, Aufgabe 7), sofern es wählbar ist. Sonst wie
    // bisher: Bei genau einem Unternehmen (der häufige Fall: eine Kundenbeziehung) ist es
    // vorgewählt; bei mehreren muss bewusst gewählt werden.
    companyId: companies.find((c) => c.id === arbeitsunternehmenId)?.id
      ?? (companies.length === 1 ? companies[0]!.id : ""),
    vorlageId: "",
    title: "", netAmount: "", contactId: "", responsibleUserId: "", validUntil: "", note: "",
    introText: "", outroText: "",
    termValue: "", termUnit: "MONTHS",
    minimumTermValue: "", minimumTermUnit: "MONTHS",
    renewalPeriodValue: "", renewalPeriodUnit: "MONTHS",
    noticePeriodValue: "", noticePeriodUnit: "MONTHS", noticeReference: "TERM_END",
    periodBasis: "CALENDAR_YEAR",
  };
}

export function angebotEntwurfAus(a: Angebot): AngebotEntwurf {
  return {
    companyId: a.companyId,
    // Beim Bearbeiten trägt das Formular KEINE Vorlage: Ein bestehendes Angebot wird nicht
    // „aus einer Vorlage angelegt" — dafür gibt es den Anwenden-Weg, der anhängt.
    vorlageId: "",
    title: a.title,
    // Deutsch geschrieben, weil deutsch gelesen wird (siehe betragText) — `String()` ergäbe
    // „200.55", und das läse `betragZahl` als 20055.
    netAmount: betragText(a.netAmount),
    contactId: a.contactId ?? "",
    responsibleUserId: a.responsibleUserId ?? "",
    validUntil: a.validUntil ?? "",
    note: a.note ?? "",
    introText: a.introText ?? "",
    outroText: a.outroText ?? "",
    termValue: a.termValue === null ? "" : String(a.termValue),
    termUnit: a.termUnit ?? "MONTHS",
    minimumTermValue: a.minimumTermValue === null ? "" : String(a.minimumTermValue),
    minimumTermUnit: a.minimumTermUnit ?? "MONTHS",
    renewalPeriodValue: a.renewalPeriodValue === null ? "" : String(a.renewalPeriodValue),
    renewalPeriodUnit: a.renewalPeriodUnit ?? "MONTHS",
    noticePeriodValue: a.noticePeriodValue === null ? "" : String(a.noticePeriodValue),
    noticePeriodUnit: a.noticePeriodUnit ?? "MONTHS",
    noticeReference: a.noticeReference ?? "TERM_END",
    periodBasis: a.periodBasis,
  };
}

/**
 * Die Kopffelder der Angebotsmaske: SERVER-Feldname → Endung der Feld-Id im Formular.
 *
 * Sie steht hier, unmittelbar neben der Prüfung, die dieselbe Tabelle benutzt — die
 * Begründung im Einzelnen bei `KopffeldTabelle` (vorgangsdetail.ts). Kurz: Es gibt zwei
 * Quellen von Feldbefunden (diese Maske und der Server), beide müssen den eingefalteten
 * Kopfblock aufklappen, und beide gehen deshalb durch diese eine Tabelle.
 *
 * Notiz, Einleitungs- und Ausleitungstext stehen bewusst NICHT darin: Sie liegen ausserhalb
 * des Kopfblocks und sind immer zu sehen.
 */
export const ANGEBOT_KOPFFELDER: KopffeldTabelle = {
  companyId: "company",
  title: "title",
  netAmount: "amount",
  contactId: "contact",
  responsibleUserId: "user",
  validUntil: "valid",
  periodBasis: "periodbasis",
  ...LAUFZEIT_KOPFFELDER,
  // Die Felder des Kopfblocks PARTEIEN (Adressen Etappe 3) — sie stehen IM Kopfblock und
  // werden in einem zweiten Weg gespeichert (`setBelegParteien`). Ein Befund von dort
  // muss denselben Weg ins Bild finden wie einer am Titel, sonst stünde die Meldung über
  // einem eingeklappten Feld.
  ...PARTEIEN_KOPFFELDER,
  // Der Abschnitt ZAHLPLAN (Migration 026) — dieselbe Überlegung wie an PARTEIEN_KOPFFELDER:
  // ein dritter Speicherweg, dessen Befunde denselben Weg ins Bild finden müssen. Die
  // SERVER-Feldnamen tragen einen Ratenindex (`raten[2].sharePercent`) und passen in keine
  // feste Tabelle — deshalb behandelt `zeigeAngebotsFehlerFeld` sie VOR dieser Tabelle
  // gesondert (`istZahlplanFeld`). Der Eintrag hier bleibt trotzdem stehen: Er hält
  // `AUFTRAG_KOPFFELDER`/`ANGEBOT_KOPFFELDER` beisammen mit `ZAHLPLAN_KOPFFELDER` und trägt
  // die einzige Kennung, die `kopffeldId` für „raten" (ohne Index) kennt.
  ...ZAHLPLAN_KOPFFELDER,
  // Der Kasten „Anhänge" — heute LEER (er hat kein Kopffeld, die Anlagen stehen unter
  // dem Zahlplan). Der Spread steht trotzdem hier, damit ein späteres Kopffeld des
  // Kastens nicht an zwei Stellen nachgetragen werden muss (Muster `PARTEIEN_KOPFFELDER`).
  ...ANHAENGE_KOPFFELDER,
};

/**
 * Der Befund der Feldprüfung: der Satz für den Bearbeiter UND das Feld, um das es geht —
 * bei seinem SERVERNAMEN, damit Masken- und Serverweg durch dieselbe Tabelle laufen
 * (`ANGEBOT_KOPFFELDER`).
 *
 * Das Feld kam mit dem eingefalteten Kopfblock dazu (Paket „Positionen mit Präsenz",
 * 08.09.2026): Alle drei Pflichtangaben stehen darin, und eingeklappt sähe niemand den
 * Satz an einem Feld, das gar nicht zu sehen ist.
 */
export type AngebotBefund = {
  meldung: string;
  feld: "companyId" | "title" | "netAmount";
};

/** Die gemeinsame Prüfung für Anlegen und Bearbeiten (null = in Ordnung). */
export function pruefeAngebot(
  entwurf: AngebotEntwurf, brauchtCompany: boolean,
): AngebotBefund | null {
  if (brauchtCompany && entwurf.companyId === "") {
    return { meldung: "Bitte ein Unternehmen wählen.", feld: "companyId" };
  }
  if (entwurf.title.trim() === "") {
    return { meldung: "Bitte einen Titel angeben.", feld: "title" };
  }
  if (Number.isNaN(betragZahl(entwurf.netAmount))) {
    return { meldung: "Bitte einen gültigen Betrag angeben.", feld: "netAmount" };
  }
  return null;
}

/**
 * Den Kopfblock auf das Feld öffnen, das ein Befund nennt — der eine Handgriff für BEIDE
 * Quellen (Maskenprüfung und Server-422).
 *
 * `feld` ist entweder der Servername aus `pruefeAngebot` oder der Fehler selbst; steht das
 * Feld nicht im Kopfblock (Notiz, Texte, Vorlage) oder trägt der Fehler gar keine Befunde
 * (Netzabbruch), geschieht nichts — die Meldung am Fuß steht dann über einem Feld, das
 * ohnehin zu sehen ist.
 */
export function zeigeAngebotsFeld(
  kopf: Kopffaltung, praefix: string, feld: string,
): void {
  const ziel = kopffeldId(ANGEBOT_KOPFFELDER, praefix, feld);
  if (ziel !== null) kopf.zeigeFeld(ziel);
}

export function zeigeAngebotsFehlerFeld(
  kopf: Kopffaltung, praefix: string, fehler: unknown,
): void {
  // Ein Befund am ZAHLPLAN trägt einen Ratenindex (`raten[2].sharePercent`) und passt in
  // keine feste Tabelle — `istZahlplanFeld` erkennt ihn VOR dem Tabellen-Nachschlag, sonst
  // fände `kopffeldAusFehler` nie eine Übereinstimmung und der Abschnitt bliebe zu.
  if (fehler instanceof ApiError
      && (fehler.fields ?? []).some((f) => istZahlplanFeld(f.field))) {
    kopf.zeigeFeld(`${praefix}-zahlplan`);
    return;
  }
  const ziel = kopffeldAusFehler(ANGEBOT_KOPFFELDER, praefix, fehler);
  if (ziel !== null) kopf.zeigeFeld(ziel);
}

export type AngebotKopffelder = {
  title: string; netAmount: number; contactId: string | null;
  responsibleUserId: string | null; validUntil: string | null; note: string | null;
  introText: string | null; outroText: string | null;
  termValue: number | null; termUnit: "MONTHS" | "YEARS" | null;
  minimumTermValue: number | null; minimumTermUnit: "MONTHS" | "YEARS" | null;
  renewalPeriodValue: number | null; renewalPeriodUnit: "MONTHS" | "YEARS" | null;
  noticePeriodValue: number | null;
  noticePeriodUnit: "MONTHS" | "YEARS" | null;
  noticeReference: "TERM_END" | "CALENDAR_YEAR_END" | null;
  periodBasis: "CALENDAR_YEAR" | "CONTRACT_YEAR";
};

/** Die Kopffelder, die Anlegen und Ändern teilen (companyId gehört nur zum Anlegen — der
 *  Server lässt sie beim Ändern nicht zu). */
export function angebotKopffelder(entwurf: AngebotEntwurf): AngebotKopffelder {
  /** Ein Wert/Einheit-PAAR für den Server: leer heisst BEIDES null. Ausgeschrieben und
   *  nicht gestreut, weil `exactOptionalPropertyTypes` ein `undefined` anders behandelt
   *  als einen fehlenden Schlüssel. */
  function paar(wert: string, einheit: "MONTHS" | "YEARS") {
    const kern = wert.trim();
    return kern === ""
      ? { wert: null, einheit: null }
      : { wert: Number(kern), einheit };
  }
  const term = paar(entwurf.termValue, entwurf.termUnit);
  const mindest = paar(entwurf.minimumTermValue, entwurf.minimumTermUnit);
  const verlaengerung = paar(entwurf.renewalPeriodValue, entwurf.renewalPeriodUnit);
  const frist = paar(entwurf.noticePeriodValue, entwurf.noticePeriodUnit);
  return {
    title: entwurf.title.trim(),
    // Mit Positionen ist der Betrag berechnet; das Feld zeigt ihn nur an und schickt ihn
    // unverändert zurück (der Server nimmt genau diesen Wert an, siehe gepruefterBetrag).
    netAmount: betragZahl(entwurf.netAmount),
    contactId: oderNull(entwurf.contactId),
    responsibleUserId: oderNull(entwurf.responsibleUserId),
    validUntil: oderNull(entwurf.validUntil),
    note: oderNull(entwurf.note),
    introText: oderNull(entwurf.introText),
    outroText: oderNull(entwurf.outroText),
    termValue: term.wert, termUnit: term.einheit,
    minimumTermValue: mindest.wert, minimumTermUnit: mindest.einheit,
    renewalPeriodValue: verlaengerung.wert, renewalPeriodUnit: verlaengerung.einheit,
    noticePeriodValue: frist.wert, noticePeriodUnit: frist.einheit,
    // Der BEZUGSPUNKT hängt weiter an der Frist: Ohne Zahl gibt es keinen Stichtag.
    noticeReference: frist.wert === null ? null : entwurf.noticeReference,
    periodBasis: entwurf.periodBasis,
  };
}

/** Die Werte für die Platzhalter-Vorschau — genau das, was die Ansicht ohnehin geladen hat.
 *  Was fehlt (z. B. eine Anschrift), erscheint gar nicht erst und bleibt damit als
 *  sichtbares Token stehen. `angebot` ist beim ANLEGEN null — eine Angebotsnummer gibt es
 *  dann noch nicht. */
export function angebotVorschauWerte(arg: {
  entwurf: AngebotEntwurf;
  companies: Unternehmen[];
  kontakte: Ansprechpartner[];
  angebot: Angebot | null;
  kundeName?: string | undefined;
}): Record<string, string> {
  const { entwurf, companies, kontakte, angebot, kundeName } = arg;
  const werte: Record<string, string> = {
    ...laufzeitVorschauWerte({
      termValue: entwurf.termValue, termUnit: entwurf.termUnit,
      minimumTermValue: entwurf.minimumTermValue, minimumTermUnit: entwurf.minimumTermUnit,
      renewalPeriodValue: entwurf.renewalPeriodValue,
      renewalPeriodUnit: entwurf.renewalPeriodUnit,
      noticePeriodValue: entwurf.noticePeriodValue,
      noticePeriodUnit: entwurf.noticePeriodUnit,
      noticeReference: entwurf.noticeReference,
    }),
    "datum.heute": formatDatum(new Date().toISOString()),
  };
  if (kundeName !== undefined && kundeName !== "") werte["kunde.name"] = kundeName;
  const firma = companies.find((c) => c.id === entwurf.companyId);
  if (firma !== undefined) werte["firma.name"] = firma.name;
  const kontakt = kontakte.find((k) => k.id === entwurf.contactId);
  if (kontakt !== undefined) {
    werte["ansprechpartner.name"] = kontakt.anzeigename;
    // Migration 025: die Briefanrede steht NEBEN dem Namen (masterdataRules.ts) — sie ist
    // eine Ableitung aus Geschlecht, Titel und Namen, kein eigenes Eingabefeld.
    werte["ansprechpartner.anrede"] = kontakt.briefanrede;
  }
  if (angebot !== null) werte["angebot.nummer"] = angebot.offerNumber;
  if (entwurf.validUntil !== "") {
    werte["angebot.gueltigBis"] = formatDatum(entwurf.validUntil);
  }
  return werte;
}

export type AngebotFelderArgs = {
  praefix: string;
  mitUnternehmen: boolean;
  companies: Unternehmen[];
  umfeld: AngebotsUmfeld;
  entwurf: AngebotEntwurf;
  setEntwurf: Dispatch<SetStateAction<AngebotEntwurf>>;
  /** Hat der Vorgang Positionen? Dann ist der Nettobetrag berechnet und nur noch Anzeige. */
  hatPositionen: boolean;
  /** Die Sammelmeldung am Fuss der Maske (Prüfung der Maske, Fehler ohne Feldbezug). */
  feldFehler: string | null;
  /**
   * Die Befunde JE FELD (ME-Etappe 3, Aufgabe 4): Servername → Satz, am Feld gezeigt
   * (`fehler` an me-eingabe/me-auswahl: aria-invalid + aria-describedby). Quelle sind die
   * 422-Befunde des Servers (`useFormularZustand.feldFehler`) und die Prüfung der Maske.
   */
  fehlerJeFeld?: Readonly<Record<string, string>> | undefined;
  /**
   * Die Formularzustände für das Raster der Maske (`me-formular`): `laeuft` sperrt die Felder
   * während eines Aufrufs, `fremderStand` zeigt nach einer 409 die Hinweiszeile mit
   * „Neu laden“. Die Anlage reicht nur `laeuft` herein.
   */
  formular?: {
    ref?: RefObject<FormularElement | null>;
    laeuft: boolean;
    fremderStand?: boolean;
    onNeuLaden?: () => void;
    hinweis?: ReactNode;
  } | undefined;
  vorschauWerte: Record<string, string>;
  /** Der Griff auf die Einfaltung des Kopfblocks — er gehört der MASKE (siehe
   *  components/Kopffaltung.tsx), weil nur sie den Startzustand und die Rücksetzpunkte
   *  kennt. */
  kopf: Kopffaltung;
  /**
   * Der POSITIONSBLOCK, der zwischen Einleitungs- und Ausleitungstext steht (Entscheid Dirk
   * 08.09.2026: dort wird die Arbeit geleistet, also bekommt er die Mitte des Formulars).
   *
   * Als Schlitz und nicht hier gezeichnet: Der Editor hängt am geladenen Vorgang samt
   * seiner Version, und beim ANLEGEN gibt es beides noch nicht — dort bleibt der Schlitz
   * leer, und die Reihenfolge der übrigen Blöcke ist trotzdem dieselbe. Die Alternative
   * wäre ein zweiter Aufbau der Maske je Weg gewesen, also genau die Kopie, die sich beim
   * dritten Fix auseinanderlebt.
   */
  positionen?: ReactNode | undefined;
  /**
   * Der Kopfblock PARTEIEN (Käufer, Anschriften, Bestellnummer) — als Schlitz und nicht
   * hier gezeichnet, aus demselben Grund wie `positionen`: Er hängt am geladenen Vorgang,
   * und beim ANLEGEN gibt es den noch nicht. Dort bleibt der Schlitz leer; die
   * Reihenfolge der übrigen Blöcke ist trotzdem dieselbe.
   */
  parteien?: ReactNode | undefined;
  /**
   * Der Abschnitt ZAHLUNGSVEREINBARUNG — als Schlitz und aus demselben Grund wie
   * `parteien`: Er hängt am geladenen Vorgang, und beim ANLEGEN gibt es den noch nicht.
   */
  zahlplan?: ReactNode | undefined;
  /**
   * Der Kasten „Anhänge" — als Schlitz aus demselben Grund wie `zahlplan`: Er hängt am
   * geladenen Vorgang, und beim ANLEGEN gibt es den noch nicht.
   *
   * Anders als `parteien` und `zahlplan` steht er NICHT im Kopfblock, sondern direkt
   * dahinter, immer sichtbar: Wer bearbeitet, will die Anlagen ohne einen weiteren Klick
   * sehen (der Kopfblock beginnt am bestehenden Vorgang eingeklappt, siehe
   * `useKopffaltung(false)` — ein Kasten DARIN wäre bis zum Aufklappen unsichtbar, und
   * genau das ist am Ausleitungstext und an den Positionen bereits die Ausnahme, keine
   * Regel). Fachlich gehört er trotzdem zu den KONDITIONEN und steht deshalb unmittelbar
   * unter dem Zahlplan, dem letzten Feld im Kopfblock (Spec: „Kasten Anhänge unter dem
   * Zahlplan").
   */
  anhaenge?: ReactNode | undefined;
};

/**
 * Die Felder von Anlege- und Bearbeitungsmaske. Bewusst eine Funktion, die JSX ZURÜCKGIBT,
 * und keine eigene Komponente: eine im Render definierte Komponente bekäme bei jedem
 * Neuzeichnen eine neue Identität — React hängte ihren Teilbaum ab und neu auf, und der
 * Fokus spränge nach jedem getippten Zeichen aus dem Feld (Lehre aus KundeAnsprechpartner).
 * Sie ist auch nach dem Herauslösen des Bausteins gemeinsam: Der Rahmen zeichnet mit ihr
 * seine ANLAGE, das Detail sein Bearbeiten.
 *
 * **Die Reihenfolge ist der Inhalt dieses Pakets** (Entscheid Dirk 08.09.2026, „Positionen
 * mit Präsenz"): eingefalteter Kopfblock, dann Notiz und Einleitungstext, dann die
 * POSITIONEN, dann der Ausleitungstext. Sie steht genau EINMAL — hier — und gilt damit für
 * Akte wie Bereich, Anlegen wie Bearbeiten.
 */
export function angebotFormularFelder(a: AngebotFelderArgs): ReactElement {
  const { praefix, umfeld, entwurf, setEntwurf } = a;
  function setze(feld: keyof AngebotEntwurf) {
    return (e: ChangeEvent<HTMLTextAreaElement>) =>
      setEntwurf((v) => ({ ...v, [feld]: e.target.value }));
  }
  const f = a.formular;
  return (
    /* Das Raster der Maske (`me-formular`, ME-Etappe 3, Aufgabe 4) — mit der Beschriftung
       OBEN (Ruling: Die Hüllen beschriften sich selbst, „links“ erwartete `<label for>`-
       Paare). Es umschliesst die GANZE Maske, nicht nur die Kopffelder: Seine Hinweiszeile
       „fremder Stand“ muss sichtbar sein, auch wenn der Kopfblock zugeklappt ist, und
       `zumErstenFehler()` sucht über alle Blöcke. */
    <Formular beschriftung="oben"
      {...(f?.ref !== undefined ? { ref: f.ref } : {})}
      laeuft={f?.laeuft === true} fremderStand={f?.fremderStand === true}
      {...(f?.onNeuLaden !== undefined ? { onNeuLaden: f.onNeuLaden } : {})}
      {...(f?.hinweis !== undefined ? { hinweis: f.hinweis } : {})}>
      <div className="kang__maske">
        <Kopfblock faltung={a.kopf} titel={entwurf.title}
          betragLabel={nettobetragLabel(a.hatPositionen)}
          betrag={kopfBetragText(entwurf.netAmount)}
          inhaltId={`${praefix}-kopfdaten`}
          fehler={kopfFehlerZahl(ANGEBOT_KOPFFELDER, a.fehlerJeFeld)}>
          {angebotKopffeldRaster(a)}
          {a.parteien}
          {a.zahlplan}
        </Kopfblock>

        {/* Der Kasten „Anhänge" — direkt hinter dem Kopfblock und OHNE eigenen Aufklapper
            (siehe die Begründung an `anhaenge` in `AngebotFelderArgs`): Wer bearbeitet, soll
            die Anlagen sehen, ohne zuerst den eingeklappten Kopfblock zu öffnen. */}
        {a.anhaenge}

        {/* Notiz und Einleitungstext gleich unter dem Kopfblock: Sie sind der Text, der den
            Vorgang eröffnet, und sie werden beim Bearbeiten häufig angefasst — anders als
            Laufzeit und Kündigungsfrist, die einmal stehen und dann stehen bleiben. */}
        <div className="kang__texte">
          {/* ÜBERGANG (Ruling Aufgabe 4): Die Notiz bleibt ein eigenes `<textarea>` — ein
              bestehender Wert trägt oft Zeilenumbrüche, und me-eingabe verliert sie, wenn
              `wert` vor `mehrzeilig` ankommt (me-design-Befund aus Aufgabe 2). */}
          <div className="kang__feld">
            <label htmlFor={`${praefix}-note`} className="kang__label">Notiz</label>
            <textarea id={`${praefix}-note`} className="kang__textarea" rows={3}
              value={entwurf.note} onChange={setze("note")} />
          </div>

          {/* Einleitungstext: Rohtext mit Tokens, darunter die leichte Vorschau. Beides kommt
              aus der wiederverwendeten Editor-Komponente — in dieser Ansicht nachgebaut, gäbe
              es die Vorschau zweimal. */}
          <PlatzhalterTextfeld id={`${praefix}-intro`} label="Einleitungstext"
            wert={entwurf.introText}
            onChange={(wert) => setEntwurf((v) => ({ ...v, introText: wert }))}
            variablen={umfeld.platzhalter} werte={a.vorschauWerte} />
        </div>

        {a.positionen}

        {/* Der Ausleitungstext schliesst den Beleg ab — also steht er hinter dem, was er
            abschliesst. Seine Hinweiszeilen bleiben an IHM: Seit die Positionen dazwischen
            stehen, wäre eine gemeinsame Fussnote für beide Textfelder eine Fussnote, die zu
            einem Feld gehört, das man von ihr aus nicht mehr sieht. */}
        <div className="kang__texte">
          <PlatzhalterTextfeld id={`${praefix}-outro`} label="Ausleitungstext"
            wert={entwurf.outroText}
            onChange={(wert) => setEntwurf((v) => ({ ...v, outroText: wert }))}
            variablen={umfeld.platzhalter} werte={a.vorschauWerte} />
        </div>

        {/* Die Sammelmeldung steht am Ende der Maske, dicht über der Fußleiste: Dort wurde
            „Speichern" gedrückt, und dort wird sie gesucht. Befunde an einzelnen Feldern
            stehen AM FELD; betrifft einer ein Feld im eingeklappten Kopfblock, hat die
            Maske ihn bereits aufgeklappt (Fehlerzähler in der Kopfzeile, Fokus aufs Feld). */}
        {a.feldFehler !== null && <Hinweis art="fehler">{a.feldFehler}</Hinweis>}
      </div>
    </Formular>
  );
}

/**
 * Wie viele Felder im KOPFBLOCK einen Befund tragen — der Fehlerzähler seiner Kopfzeile
 * (ME-Etappe 3, Aufgabe 4, Review-Fokus 3). Gezählt wird über die Kopffeld-Tabelle der Maske
 * (dieselbe, die das Aufklappen steuert); die Ratenbefunde des Zahlplans tragen einen Index
 * (`raten[2].sharePercent`) und zählen über `istZahlplanFeld`.
 */
export function kopfFehlerZahl(
  tabelle: KopffeldTabelle, fehler: Readonly<Record<string, string>> | undefined,
): number {
  if (fehler === undefined) return 0;
  return Object.keys(fehler)
    .filter((feld) => tabelle[feld] !== undefined || istZahlplanFeld(feld)).length;
}

/** Die vier Laufzeit-Einheiten (Migration 031) — EINE Liste für alle vier Paare. */
const EINHEITEN: { wert: "MONTHS" | "YEARS"; text: string }[] = [
  { wert: "MONTHS", text: "Monate" },
  { wert: "YEARS", text: "Jahre" },
];

/**
 * Ein Laufzeit-PAAR aus Zahl und Einheit (Migration 031) — geteilt mit der Auftragsmaske,
 * damit beide Vorgangsarten dieselben vier Paare zeichnen. Die Zahl ist ein `Feld` (Art
 * `zahl`), die Einheit eine kompakte `Auswahl` (Beschriftung nur für die Vorlesestimme):
 * Zahl und Einheit stehen nebeneinander, zwei Zeilen wären zwei Angaben.
 */
export function laufzeitPaar(arg: {
  id: string; label: string; einheitLabel: string;
  wert: string; einheit: "MONTHS" | "YEARS";
  onWert: (wert: string) => void; onEinheit: (einheit: "MONTHS" | "YEARS") => void;
  fehler?: string | undefined; einheitFehler?: string | undefined;
  /** Klassenblock des Wirts (`kang`/`kauf`) — die Gestalt des Paares bleibt dort. */
  block: string;
}): ReactElement {
  return (
    <div className={`${arg.block}__feld ${arg.block}__feld--paar`}>
      <div className={`${arg.block}__paar`}>
        <Feld id={arg.id} label={arg.label} typ="zahl" bearbeitbar wert={arg.wert}
          onChange={arg.onWert}
          {...(arg.fehler !== undefined ? { fehler: arg.fehler } : {})} />
        <Auswahl id={`${arg.id}-unit`} name={`${arg.id}-unit`} kompakt
          beschriftung={arg.einheitLabel} wert={arg.einheit}
          {...(arg.einheitFehler !== undefined ? { fehler: arg.einheitFehler } : {})}
          onGeaendert={(d) => arg.onEinheit(d.wert === "YEARS" ? "YEARS" : "MONTHS")}>
          {EINHEITEN.map((e) => <option key={e.wert} value={e.wert}>{e.text}</option>)}
        </Auswahl>
      </div>
    </div>
  );
}

/** `fehler`-Prop eines Feldes aus der Befundkarte — ausgeschrieben, weil
 *  `exactOptionalPropertyTypes` ein `fehler: undefined` nicht zulässt. */
export function feldFehlerProp(
  karte: Readonly<Record<string, string>> | undefined, feld: string,
): { fehler?: string } {
  const text = karte?.[feld];
  return text === undefined ? {} : { fehler: text };
}

/** Das zweispaltige Raster der KOPFFELDER — der Inhalt des einfaltbaren Blocks. Eigene
 *  Funktion allein der Lesbarkeit halber: Sie hat keinen eigenen Zustand und keine eigene
 *  Identität (kein Komponententyp), zeichnet also weiterhin im selben Zug wie die Maske.
 *
 *  Seit ME-Etappe 3 (Aufgabe 4) auf den Hüllen: `Feld` (me-eingabe) für Text, Betrag,
 *  Datum und Zahlen, `Auswahl` (me-auswahl) statt der rohen `<select>`. Beide beschriften
 *  sich selbst; die Kennungen bleiben (`kopf.zeigeFeld` fokussiert über sie). */
function angebotKopffeldRaster(a: AngebotFelderArgs): ReactElement {
  const { praefix, companies, umfeld, entwurf, setEntwurf, hatPositionen } = a;
  const fj = a.fehlerJeFeld;
  function setze<K extends keyof AngebotEntwurf>(feld: K) {
    return (wert: AngebotEntwurf[K]) => setEntwurf((v) => ({ ...v, [feld]: wert }));
  }
  return (
    <div className="kang__felder">
      {a.mitUnternehmen && (
        <Auswahl id={`${praefix}-company`} name={`${praefix}-company`}
          beschriftung="Unternehmen" wert={entwurf.companyId} {...feldFehlerProp(fj, "companyId")}
          onGeaendert={(d) => setze("companyId")(d.wert)}>
          {companies.length !== 1 && <option value="">Bitte wählen …</option>}
          {companies.map((c) => (
            <option key={c.id} value={c.id}>{c.shortCode} · {c.name}</option>
          ))}
        </Auswahl>
      )}

      <Feld id={`${praefix}-title`} label="Titel" bearbeitbar wert={entwurf.title}
        onChange={setze("title")} {...feldFehlerProp(fj, "title")} />

      {/* Sobald der Vorgang Positionen hat, ist der Betrag BERECHNET: Das Feld zeigt ihn
          nur noch an. Von Hand gesetzt wäre er eine zweite Wahrheit — der Server weist ihn
          als Feldfehler ab (BETRAG_BERECHNET), und die Maske soll das gar nicht erst
          anbieten. `gesperrt` (readOnly + aria-disabled) statt `disabled`: Der Wert bleibt
          lesbar, kopierbar und wird beim Speichern unverändert mitgeschickt (genau das
          erwartet der Server). Ein Betrag ist TEXT (deutsches Komma), kein Zahlenfeld. */}
      <Feld id={`${praefix}-amount`} label={nettobetragLabel(hatPositionen)} bearbeitbar
        wert={entwurf.netAmount} onChange={setze("netAmount")} gesperrt={hatPositionen}
        {...feldFehlerProp(fj, "netAmount")} />

      <Auswahl id={`${praefix}-contact`} name={`${praefix}-contact`}
        beschriftung="Ansprechpartner" wert={entwurf.contactId}
        {...feldFehlerProp(fj, "contactId")} onGeaendert={(d) => setze("contactId")(d.wert)}>
        <option value="">— keiner —</option>
        {umfeld.kontakte.map((k) => (
          <option key={k.id} value={k.id}>{k.anzeigename}</option>
        ))}
      </Auswahl>

      <Auswahl id={`${praefix}-user`} name={`${praefix}-user`}
        beschriftung="Verantwortlich" wert={entwurf.responsibleUserId}
        {...feldFehlerProp(fj, "responsibleUserId")}
        onGeaendert={(d) => setze("responsibleUserId")(d.wert)}>
        <option value="">— niemand —</option>
        {umfeld.benutzer.map((b) => (
          <option key={b.id} value={b.id}>{b.name}</option>
        ))}
      </Auswahl>

      <Feld id={`${praefix}-valid`} label="Gültig bis" typ="date" bearbeitbar
        wert={entwurf.validUntil} onChange={setze("validUntil")}
        {...feldFehlerProp(fj, "validUntil")} />

      {/* DIE LAUFZEIT (Migration 031, Entscheid E1): vier Dauern, jede als Zahl PLUS
          Einheit. Die Vertragslaufzeit steht zuerst — sie ist die Aussage, die ein Kunde
          als erste sucht, und die Spec nennt sie ausdrücklich als erstes Feld. */}
      {laufzeitPaar({ block: "kang", id: `${praefix}-term`, label: "Vertragslaufzeit",
        einheitLabel: "Einheit der Vertragslaufzeit", wert: entwurf.termValue,
        einheit: entwurf.termUnit, onWert: setze("termValue"), onEinheit: setze("termUnit"),
        fehler: fj?.["termValue"], einheitFehler: fj?.["termUnit"] })}
      {laufzeitPaar({ block: "kang", id: `${praefix}-minterm`, label: "Mindestlaufzeit",
        einheitLabel: "Einheit der Mindestlaufzeit", wert: entwurf.minimumTermValue,
        einheit: entwurf.minimumTermUnit, onWert: setze("minimumTermValue"),
        onEinheit: setze("minimumTermUnit"),
        fehler: fj?.["minimumTermValue"], einheitFehler: fj?.["minimumTermUnit"] })}
      {laufzeitPaar({ block: "kang", id: `${praefix}-renewal`, label: "Verlängerung",
        einheitLabel: "Einheit der Verlängerung", wert: entwurf.renewalPeriodValue,
        einheit: entwurf.renewalPeriodUnit, onWert: setze("renewalPeriodValue"),
        onEinheit: setze("renewalPeriodUnit"),
        fehler: fj?.["renewalPeriodValue"], einheitFehler: fj?.["renewalPeriodUnit"] })}
      {laufzeitPaar({ block: "kang", id: `${praefix}-notice`, label: "Kündigungsfrist",
        einheitLabel: "Einheit der Kündigungsfrist", wert: entwurf.noticePeriodValue,
        einheit: entwurf.noticePeriodUnit, onWert: setze("noticePeriodValue"),
        onEinheit: setze("noticePeriodUnit"),
        fehler: fj?.["noticePeriodValue"], einheitFehler: fj?.["noticePeriodUnit"] })}

      {/* Der Bezugspunkt „Kündigung wirkt" bleibt UNVERÄNDERT an seiner Stelle hinter der
          Frist — er ist kein eigenes Wert/Einheit-Paar, sondern die Wahl, worauf sich die
          Frist bezieht. */}
      <div className="kang__feld kang__feld--breit">
        <Auswahl id={`${praefix}-notice-ref`} name={`${praefix}-notice-ref`}
          beschriftung="Kündigung wirkt" wert={entwurf.noticeReference}
          {...feldFehlerProp(fj, "noticeReference")}
          onGeaendert={(d) => setze("noticeReference")(
            d.wert === "CALENDAR_YEAR_END" ? "CALENDAR_YEAR_END" : "TERM_END")}>
          <option value="TERM_END">zum Ende der Laufzeit</option>
          <option value="CALENDAR_YEAR_END">zum Ende des Kalenderjahres</option>
        </Auswahl>
      </div>

      {/* Die Periodenregel des Zahlplans (Migration 026) — sie gilt für den ganzen
          Beleg, deshalb steht sie am Kopf und nicht je Rate im Abschnitt darunter. */}
      <div className="kang__feld kang__feld--breit">
        <Auswahl id={`${praefix}-periodbasis`} name={`${praefix}-periodbasis`}
          beschriftung="Leistungszeitraum" wert={entwurf.periodBasis}
          {...feldFehlerProp(fj, "periodBasis")}
          onGeaendert={(d) => setze("periodBasis")(
            d.wert === "CONTRACT_YEAR" ? "CONTRACT_YEAR" : "CALENDAR_YEAR")}>
          <option value="CALENDAR_YEAR">Kalenderjahr</option>
          <option value="CONTRACT_YEAR">Vertragsjahr</option>
        </Auswahl>
        <p className="kang__hinweis">
          Bei Kalenderjahr zählen erstes und letztes Jahr VOLL — Beträge und Rechnungen
          werden für das ganze Kalenderjahr aufgenommen.
        </p>
      </div>
    </div>
  );
}

export type AngebotDetailProps = {
  client: AngebotDetailClient;
  kundeId: string;
  /** Der Vorgang — fertig geladen, samt seiner `version` (siehe Kopf der Datei). */
  angebot: Angebot;
  /** Die Unternehmen mit Kundenbeziehung — für das Kürzel und die Platzhalter-Vorschau. */
  companies: Unternehmen[];
  /** Die vom Rahmen geladenen Auswahllisten (`useAngebotsUmfeld`). */
  umfeld: AngebotsUmfeld;
  /** Steht GENAU DIESER Vorgang gerade in Bearbeitung? Die Entscheidung gehört dem Rahmen —
   *  nur so bleibt es bei einem offenen Formular je Ansicht. */
  bearbeitung: boolean;
  /** Ist die Bedienung gesperrt, weil anderswo ein Formular offen ist? */
  gesperrt: boolean;
  /** Der Firmenname des Kunden — allein für die Platzhalter-Vorschau (`#{kunde.name}`). */
  kundeName?: string | undefined;
  onBearbeiten: () => void;
  onSchliessen: () => void;
  /** Der Rahmen soll seine Liste neu holen (nach Statuswechsel, Speichern, 409). */
  onNeuLaden: () => void | Promise<unknown>;
  /** Der Meldungsstreifen gehört dem Rahmen — er steht über der ganzen Ansicht, nicht am
   *  einzelnen Vorgang. */
  onHinweis: (text: string | null) => void;
  /** Der Vorgang ist weitergedreht (Version/Betrag aus dem Positions-Editor, frischer Kopf
   *  nach „Vorlage anwenden"): Der Rahmen schreibt ihn in seine Liste fort und reicht ihn
   *  als `angebot` zurück herein. Ohne das rechneten die Statusknöpfe mit einer veralteten
   *  Version und meldeten „zwischenzeitlich geändert" für die eigene Änderung. */
  onFortschreiben: (angebot: Angebot) => void;
  /**
   * Der Vorgang ist durch eine ANDERE Kennung ERSETZT worden — „Neue Version" oder
   * „Kopieren" (Paket Angebotsversionen, Fixrunde 1 zu Aufgabe 6). Anders als
   * `onFortschreiben` (derselbe Vorgang, neuer Stand) wechselt hier die Kennung selbst:
   * Der Rahmen muss seinen Bezug — und, wo es eine Adresse gibt, sie selbst — auf den
   * NEUEN Vorgang umstellen und seine Liste nachziehen (Global Constraint „der Rahmen
   * lädt seine Liste neu und öffnet die neue Version"). Ein blosses `onNeuLaden` griffe
   * daneben: Es liest die Kennung, die der Rahmen GERADE führt — und das ist noch die
   * alte, jetzt abgelöste.
   */
  onErsetzt: (angebot: Angebot) => void;
  /** Die Angebot→Auftrag-Naht (Ruling P3A-B): am ACCEPTED-Angebot ruft „Auftrag anlegen"
   *  ihn mit den Angebotsdaten — der Baustein erzeugt selbst KEINEN Auftrag. */
  onAuftragAusAngebot: (angebot: Angebot) => void;
};

/**
 * Das Präfix der Feld-Kennungen dieser Maske — einmal benannt, weil daraus auch das Ziel
 * des Auto-Aufklappens gebildet wird (`kopf.zeigeFeld`). Zweimal getippt wäre es beim
 * nächsten Umbenennen einmal falsch, und dann klappte der Kopfblock auf ein Feld auf, das
 * es nicht gibt.
 */
const BEARBEITEN_PRAEFIX = "kang-bearbeiten";

/**
 * Die Beschriftungen je SERVER-Feldname — für die Sammelmeldung des Formularzustands
 * (`useFormularZustand.beschriftungen`, Ruling Aufgabe 3/4): Ein Befund, der an keinem Feld
 * steht, nennt die Beschriftung, die der Benutzer sieht — nie den technischen Namen.
 * Geteilt mit der Auftragsmaske (`AUFTRAG_BESCHRIFTUNGEN` baut darauf auf).
 */
export const VORGANG_BESCHRIFTUNGEN: Readonly<Record<string, string>> = {
  companyId: "Unternehmen", title: "Titel", netAmount: "Nettobetrag",
  contactId: "Ansprechpartner", responsibleUserId: "Verantwortlich",
  introText: "Einleitungstext", outroText: "Ausleitungstext",
  termValue: "Vertragslaufzeit", termUnit: "Einheit der Vertragslaufzeit",
  minimumTermValue: "Mindestlaufzeit", minimumTermUnit: "Einheit der Mindestlaufzeit",
  renewalPeriodValue: "Verlängerung", renewalPeriodUnit: "Einheit der Verlängerung",
  noticePeriodValue: "Kündigungsfrist", noticePeriodUnit: "Einheit der Kündigungsfrist",
  noticeReference: "Kündigung wirkt", periodBasis: "Leistungszeitraum",
};

export const ANGEBOT_BESCHRIFTUNGEN: Readonly<Record<string, string>> = {
  ...VORGANG_BESCHRIFTUNGEN, validUntil: "Gültig bis", note: "Notiz",
};

/**
 * Die Felder, die die BEARBEITUNGSMASKE als Hülle mit eigenem Fehlerplatz zeichnet — ein
 * Befund zu ihnen steht AM FELD (`useFormularZustand.bekannteFelder`). Alles andere (Notiz,
 * Einleitungs-/Ausleitungstext, die beim Bearbeiten nicht gezeigte Firma) landet als
 * „<Beschriftung>: <Satz>“ in der Sammelmeldung am Fuss — kein Befund verschwindet still.
 */
const ANGEBOT_FELDER_AM_FELD: readonly string[] = Object.keys(ANGEBOT_BESCHRIFTUNGEN)
  .filter((f) => !["companyId", "note", "introText", "outroText"].includes(f));

/** Der Fehlschlag eines FOLGEWEGES (Parteien, Zahlplan) im Speichern: Sein Befund steht
 *  schon im Block (`befunde`), der Formularzustand braucht nur noch den Satz. */
class FolgewegFehler extends Error {}

/** Was ein gescheiterter Folgeweg an den Formularzustand weitergibt: eine 409 als SIE SELBST
 *  (dann gilt der fremde Stand für die ganze Maske), alles andere als Satz. */
export function folgewegFehler(meldung: string, fehler: unknown): Error {
  return fehler instanceof ApiError && fehler.status === 409
    ? fehler : new FolgewegFehler(meldung);
}

/** Trägt ein 422-Befund ein Feld, das im (vielleicht zugeklappten) Kopfblock steht? */
function betrifftKopfblock(tabelle: KopffeldTabelle, fehler: unknown): boolean {
  return fehler instanceof ApiError && fehler.status === 422
    && (fehler.fields ?? []).some((f) => tabelle[f.field] !== undefined
      || istZahlplanFeld(f.field));
}

export function AngebotDetail(p: AngebotDetailProps): ReactElement {
  const { client, kundeId, angebot, companies, umfeld, bearbeitung, gesperrt } = p;

  const [entwurf, setEntwurf] = useState<AngebotEntwurf>(() => angebotEntwurfAus(angebot));
  /**
   * Die Einfaltung der Kopffelder: Beim BEARBEITEN eines bestehenden Vorgangs beginnt sie
   * ZU (Entscheid Dirk 08.09.2026) — Titel, Laufzeit und Kündigungsfrist stehen dann meist
   * schon, und die Arbeit findet an den Positionen statt, die dadurch nach oben rücken.
   */
  const kopf = useKopffaltung(false);
  /** Die Einfaltung des LESENDEN Zahlungsvereinbarung-Kastens im geschlossenen Detail
   *  (Gesamtreview, I2) — VORGABE geschlossen: Ohne sie riefe jede Zeile der Liste
   *  sofort `getZahlplan` ab, obwohl kaum je jemand hinsieht. */
  const zahlplanFaltung = useKopffaltung(false);
  /** Die Einfaltung des LESENDEN Kastens „Anhänge" im geschlossenen Detail — dieselbe
   *  Vorgabe geschlossen (Gesamtreview, I2): `Anhaenge` holt in seinem Effekt sofort
   *  `getAnhaenge` ab, sobald es gezeichnet wird — bei vielen Angeboten in der Liste wäre
   *  das ein Abruf je Zeile, den fast niemand ansieht. */
  const anhaengeFaltung = useKopffaltung(false);
  /** Der Befund der MASKENprüfung (`pruefeAngebot`) — steht am Feld UND am Fuss. Befunde
   *  des Servers führt der Formularzustand (`formular.feldFehler`/`formular.meldung`). */
  const [pruefung, setPruefung] = useState<AngebotBefund | null>(null);
  /** Der Positionsstand des Vorgangs — der Editor meldet ihn nach jedem Schritt. Er
   *  entscheidet, ob der Nettobetrag von Hand änderbar ist. */
  const [positionen, setPositionen] = useState<PositionsStand | null>(null);
  /** Welche Vorlage der Anwenden-Dialog gerade anbietet (null = zu). */
  const [anwendenWahl, setAnwendenWahl] = useState<string | null>(null);
  /** Lief im offenen Subdialog schon ein „Anwenden“? Dann gehört die Meldung des
   *  Formularzustands IHM (und steht dort), sonst der Maske darunter. */
  const [anwendenVersucht, setAnwendenVersucht] = useState(false);
  /**
   * Der Knopf „Vorlage anwenden“ — nach dem Schliessen des Subdialogs geht der Fokus
   * AUSDRÜCKLICH zurück an ihn (Fixrunde 1, E2E WebKit): Safari fokussiert Knöpfe beim
   * Mausklick nicht, die native Rückgabe des Dialogs hätte dort kein Ziel, und der Fokus
   * fiele auf <body> — hinter die offene Maske. Ausnahme: Ein 422-Befund an einem Feld der
   * Maske schliesst den Subdialog, und der Sprung zum ersten Fehler setzt den Fokus dorthin
   * (`fokusAmFeld`).
   */
  const anwendenKnopf = useRef<HTMLButtonElement>(null);
  const anwendenWarOffen = useRef(false);
  const fokusAmFeld = useRef(false);
  useEffect(() => {
    if (anwendenWahl !== null) { anwendenWarOffen.current = true; return; }
    if (!anwendenWarOffen.current) return;
    anwendenWarOffen.current = false;
    if (fokusAmFeld.current) { fokusAmFeld.current = false; return; }
    anwendenKnopf.current?.focus();
  }, [anwendenWahl]);
  /** Zählt hoch, sobald eine Vorlage angewandt wurde: Er steckt im `key` des Editors und
   *  hängt ihn damit neu auf, sodass er die angehängten Zeilen frisch lädt. Der Editor führt
   *  seinen Stand selbst — ohne das zeigte er nach dem Anwenden die alte Liste. */
  const [positionenNeuLaden, setPositionenNeuLaden] = useState(0);
  /** Läuft gerade eine Aktion an DIESEM Vorgang (sperrt seine Knöpfe)? */
  const [beschaeftigt, setBeschaeftigt] = useState(false);
  /** Die Rückmeldung des „Als PDF"-Knopfes — MIT ihrer Art (siehe PDF_MELDUNGS_STIL). */
  const [pdfMeldung, setPdfMeldung] = useState<PdfMeldung | null>(null);
  /** Ist die Vorschau offen? Sie hält keinen eigenen Stand über das Schliessen hinaus —
   *  ein `boolean` genügt, die Bilder holt sie sich selbst (Spec). */
  const [vorschauOffen, setVorschauOffen] = useState(false);
  /**
   * Der Ablagestand DIESES Angebots: `undefined` heisst „noch nicht beantwortet",
   * `null` „es gibt keine". Dieselbe Dreiteilung wie an der Rechnung
   * (`RechnungDetail`) — ein blosses `null` für beides hiesse, dass die Maske beim
   * Aufschlagen „nicht abgelegt" behauptet, bevor sie gefragt hat.
   */
  const [ablage, setAblage] = useState<BelegAblage | null | undefined>(undefined);
  /** Der Entwurf des Kopfblocks PARTEIEN — Bauart und Begründung bei `useParteienGriff`
   *  (vorgangsdetail.ts). */
  const parteien = useParteienGriff();
  /** Der Entwurf des Abschnitts ZAHLUNGSVEREINBARUNG — dieselbe Bauart, DRITTER
   *  Speicherweg (`useZahlplanGriff`, vorgangsdetail.ts). */
  const zahlplan = useZahlplanGriff();
  /** Zählt hoch, wenn „Neu laden“ den Serverstand holt: Er steckt im `key` von Parteien,
   *  Zahlplan und Anhängen und hängt sie damit neu auf — sie führen ihren Stand selbst. */
  const [standNeuLaden, setStandNeuLaden] = useState(0);

  /**
   * Der Weg, den der Formularzustand gerade geht — Speichern ODER Vorlage anwenden. Beide
   * schreiben an DEMSELBEN Vorgang mit derselben Version, also teilen sie sich EINEN
   * Zustand: Eine 409 auf dem einen sperrt auch den anderen, bis „Neu laden“.
   */
  const weg = useRef<() => Promise<boolean>>(async () => true);
  /**
   * Die Formularzustände (ME-Etappe 3, Aufgabe 4) — ersetzt den eigenen 409-Code: Eine 409
   * schliesst die Maske nicht mehr und lädt nicht still nach, sondern setzt den „fremden
   * Stand“ (Felder gesperrt, die Eingaben bleiben lesbar, „Neu laden“ in der Hinweiszeile).
   * Eine 422 legt die Befunde an die Felder, klappt den Kopfblock auf, wenn einer darin
   * steht, und springt zum ersten (Review-Fokus 3).
   */
  const formular = useFormularZustand<boolean>({
    speichern: () => weg.current(),
    neuLaden: async () => {
      const frisch = await client.getOffer(kundeId, angebot.id);
      p.onFortschreiben(frisch);
      setEntwurf(angebotEntwurfAus(frisch));
      setPruefung(null);
      setPositionen(null);
      setPositionenNeuLaden((n) => n + 1);
      parteien.zuruecksetzen();
      zahlplan.zuruecksetzen();
      setStandNeuLaden((n) => n + 1);
    },
    bekannteFelder: ANGEBOT_FELDER_AM_FELD,
    beschriftungen: ANGEBOT_BESCHRIFTUNGEN,
  });
  const speichert = formular.laeuft;

  /** Die Versionen der Reihe für den Abschnitt „Versionen" — `null` heisst „noch nicht
   *  geladen". Geladen wird NUR, wenn es überhaupt mehr als eine gibt (`versionCount`):
   *  Der Regelfall ist ein Angebot mit genau einer Fassung, und ein Abruf je
   *  Listenzeile dafür wäre reine Last. */
  const [versionen, setVersionen] = useState<AngebotVersion[] | null>(null);

  /**
   * Kein UI-Zustand überlebt den Wechsel der Angebotskennung: Der Effekt setzt beim
   * Wechsel ZUERST zurück und zählt beim Abräumen hoch (`lauf`-Ref wie in
   * `AdressListe.tsx`), damit eine spät eintreffende Antwort des VORIGEN Angebots die
   * Liste des jetzigen nicht überschreibt.
   */
  const versionsLauf = useRef(0);
  useEffect(() => {
    versionsLauf.current += 1;
    const lauf = versionsLauf.current;
    setVersionen(null);
    if (angebot.versionCount < 2) return;
    client.listOfferVersions(kundeId, angebot.id)
      .then(({ items }) => { if (versionsLauf.current === lauf) setVersionen(items); })
      .catch(() => { /* Nebenauskunft — kein Aufhalten des Details */ });
    return () => { versionsLauf.current += 1; };
  }, [client, kundeId, angebot.id, angebot.versionCount]);

  /**
   * Der Ablagestand des aufgeschlagenen Angebots. Nur an einem Angebot, das WIRKLICH
   * hinausgegangen ist — Massstab ist `sentOn`, nicht der Status (Schlussrunde I1): Ein
   * Angebot, das aus dem Entwurf direkt zurückgezogen wurde (DRAFT → WITHDRAWN), hat kein
   * `sentOn` und trotzdem einen Status ungleich DRAFT; ohne diese Prüfung erschiene dort
   * fälschlich „Noch nicht in der Akte abgelegt", obwohl der Server das Ablegen mit 422
   * abweist. Am Entwurf UND an einem nie gesendeten Angebot gibt es keine Ablage, und ein
   * Abruf wäre eine Frage mit bekannter Antwort.
   *
   * Ein Ladefehler bleibt UNBEKANNT (`undefined`) und wird nicht zur Meldung: Der
   * Ablagestand ist eine Randauskunft, und eine Fehlerzeile dafür stünde neben dem
   * Beleg, um den es geht.
   */
  const ablageLesen = useCallback(async (
    id: string, status: OfferStatus, sentOn: string | null,
  ): Promise<void> => {
    if (status === "DRAFT" || sentOn === null) { setAblage(undefined); return; }
    try {
      const { ablage: stand } = await client.getOfferAblage(kundeId, id);
      setAblage(stand);
    } catch {
      setAblage(undefined);
    }
  }, [client, kundeId]);

  useEffect(() => {
    void ablageLesen(angebot.id, angebot.status, angebot.sentOn);
    // `status` UND `sentOn` stehen in den Abhängigkeiten: `status` ändert sich beim
    // Versand (dann gibt es plötzlich eine Ablage), `sentOn` unterscheidet ein gesendetes
    // von einem nie gesendeten, zurückgezogenen Angebot bei GLEICHEM Status (WITHDRAWN).
  }, [angebot.id, angebot.status, angebot.sentOn, ablageLesen]);

  /**
   * Der Übergang „schliesst auf": Beim Öffnen der Bearbeitung wird der Entwurf frisch aus
   * dem Vorgang gebildet, und der Stand des vorigen Anlaufs verfällt.
   *
   * Bewusst WÄHREND des Zeichnens statt in einem Effekt (React: „Adjusting state when a
   * prop changes"): Ein Effekt liefe erst nach dem ersten Bild, und für einen Augenblick
   * stünden die Werte des letzten Anlaufs im Formular. `setPositionen(null)` gehört dazu —
   * sonst spränge der Nettobetrag in die Nur-Lese-Fassung, bevor der Editor geladen hat.
   */
  const [zuletztBearbeitung, setZuletztBearbeitung] = useState(bearbeitung);
  if (bearbeitung !== zuletztBearbeitung) {
    setZuletztBearbeitung(bearbeitung);
    if (bearbeitung) {
      setEntwurf(angebotEntwurfAus(angebot));
      setPruefung(null);
      formular.zuruecksetzen();
      setAnwendenWahl(null);
      setPositionen(null);
      parteien.zuruecksetzen();
      zahlplan.zuruecksetzen();
      // Und die Einfaltung mit: Wer ein Panel neu aufschlägt, findet den Kopfblock im
      // Startzustand vor — nicht so, wie er ihn beim vorigen Vorgang hinterlassen hat.
      kopf.zuruecksetzen();
      // Die Vorschau schliesst, wenn die Bearbeitung von AUSSEN beginnt (M5, Schlussrunde):
      // Ihr Schleier liegt bei z-index 60, das Vorschau-Modal bei 100 — ohne diese Zeile
      // läge das Bearbeiten-Panel HINTER der Vorschau, statt sie abzulösen.
      setVorschauOffen(false);
    }
  }

  /**
   * Der Editor meldet nach jedem Schritt die frische Version des VORGANGS und den ganzen
   * Stand. Beides muss ankommen: Die Version geht in das nächste `updateOffer` (sonst 409),
   * und aus dem Stand liest die Maske den berechneten Betrag.
   *
   * Und zwar bis in die LISTE des Rahmens (`onFortschreiben`). Die Liste zu übergehen war
   * ein Fehler (Fund der E2E-Aufgabe 6): Die Statusknöpfe und die Angebot→Auftrag-Naht
   * arbeiten mit der Version AUS DER LISTE. Wer Positionen bearbeitete und das Formular
   * dann mit „Abbrechen" schloss, bekam beim ersten Klick auf „Senden" eine 409 samt der
   * Meldung „Dieses Angebot wurde zwischenzeitlich geändert" — obwohl niemand dazwischenkam.
   */
  function positionenGeaendert(version: number, stand: PositionsStand): void {
    setPositionen(stand);
    p.onFortschreiben({ ...angebot, version, netAmount: stand.netAmount });
    setEntwurf((v) => (
      // Sobald Positionen da sind, ist der Betrag berechnet — das Anzeigefeld führt genau
      // den Wert, den `updateOffer` unverändert zurückerwartet (sonst 422 BETRAG_BERECHNET).
      stand.items.length === 0 ? v : { ...v, netAmount: betragText(stand.netAmount) }));
  }

  /** Hat der Vorgang Positionen? Entschieden an der ANZAHL, nicht am Betrag: Ein Beleg aus
   *  lauter Zugaben zu 0,00 hat Positionen (Fund G1 aus Aufgabe 2). */
  const hatPositionen = positionen !== null && positionen.items.length > 0;

  /**
   * „Vorlage anwenden" am BESTEHENDEN Angebot: Die Positionen der Vorlage werden ANGEHÄNGT,
   * Einleitungs- und Ausleitungstext übernimmt der Server nur, wenn sie leer sind.
   *
   * Danach wird der Kopf FRISCH gelesen. Das ist kein Beiwerk: Der Entwurf im Formular
   * kennt die Texte von vorher. Schickte ihn das nächste Speichern unverändert, machte es
   * die Übernahme des Servers still rückgängig (oder überschriebe einen bestehenden Text
   * mit dem alten Stand) — die Zusage „nur wenn leer" gälte dann nur bis zum nächsten Klick.
   */
  async function vorlageAnwenden(vorlageId: string): Promise<void> {
    if (vorlageId === "") return; // der Knopf ist ohne Wahl gesperrt
    setAnwendenVersucht(true);
    weg.current = async () => {
      try {
        const stand = await client.applyTemplate(
          kundeId, angebot.id, angebot.version, vorlageId);
        // Der Editor führt seinen Stand selbst — der Zähler im `key` hängt ihn neu auf,
        // damit er die angehängten Zeilen lädt. `positionenGeaendert` allein reichte nicht.
        setPositionenNeuLaden((n) => n + 1);
        setPositionen(stand);
        const frisch = await client.getOffer(kundeId, angebot.id);
        // Und in die LISTE, geradeaus statt über Umwege: Die Statusknöpfe und die
        // Angebot→Auftrag-Naht rechnen mit der Version aus der Liste.
        p.onFortschreiben(frisch);
        setEntwurf(angebotEntwurfAus(frisch));
        return true;
      } catch (e) {
        // Eine 409 gehört der MASKE (fremder Stand mit „Neu laden“) — der Subdialog geht
        // zu, sonst stünde er über der Hinweiszeile, die jetzt gilt. Andere Fehlschläge
        // bleiben im Subdialog stehen (dort wurde „Anwenden“ gedrückt).
        if (e instanceof ApiError && e.status === 409) setAnwendenWahl(null);
        // Ein 422-Befund an einem Feld der MASKE (Fixrunde 1, Review-Hinweis 2): Er steht am
        // Feld dahinter, und der Sprung zum ersten Fehler zielt dorthin — der Subdialog geht
        // im selben Zug zu, sonst bliebe er leer über einer Maske, die man nicht bedienen kann.
        if (e instanceof ApiError && e.status === 422
            && (e.fields ?? []).some((f) => ANGEBOT_FELDER_AM_FELD.includes(f.field))) {
          fokusAmFeld.current = true;
          setAnwendenWahl(null);
        }
        // Auch der SERVER kann Kopffelder bemängeln (das Anwenden schreibt den Kopf mit).
        if (betrifftKopfblock(ANGEBOT_KOPFFELDER, e)) kopf.oeffnen();
        throw e;
      }
    };
    if (await formular.aktion() !== true) return;
    setAnwendenWahl(null);
    p.onHinweis("Die Vorlage ist angewandt — ihre Positionen stehen am Ende der Liste.");
  }

  /** Der Subdialog „Vorlage anwenden“ — Abbrechen mit Rückfrage, sobald eine Vorlage gewählt
   *  ist (Hausregel striktes Modal); während des Anwendens gesperrt. */
  async function anwendenAbbrechen(): Promise<void> {
    if (speichert) return;
    if (!(await darfAbbrechen(anwendenWahl !== null && anwendenWahl !== ""))) return;
    setAnwendenWahl(null);
  }

  /** Der zweite Speicherweg im selben Klick — Reihenfolge, Version und Sinn stehen an
   *  `speichereParteien` (vorgangsdetail.ts), damit alle drei Beleg-Masken EINE Naht
   *  haben. `null` heisst: Der Kopf steht, die Parteien nicht; das Panel bleibt offen.
   *  Sonst die Version, die der Beleg NACH diesem Weg trägt — der DRITTE Weg
   *  (Zahlplan) rechnet mit ihr weiter. */
  function parteienSpeichern(
    frisch: Angebot, folgefehler: { e: unknown },
  ): Promise<number | null> {
    let version: number | null = null;
    return speichereParteien({
      client, art: "offer", customerId: angebot.customerId, vorgangId: angebot.id,
      version: frisch.version, griff: parteien,
      beiFehler: (meldung, fehler) => {
        // Der frische Vorgang muss in die Liste des Rahmens und in den Entwurf — sonst
        // rechnete der nächste Anlauf mit der alten Version und bekäme eine 409 über eine
        // Änderung, die niemand fremd gemacht hat.
        p.onFortschreiben(frisch);
        setEntwurf(angebotEntwurfAus(frisch));
        folgefehler.e = folgewegFehler(meldung, fehler);
        zeigeAngebotsFehlerFeld(kopf, BEARBEITEN_PRAEFIX, fehler);
      },
      beiErfolg: (neueVersion) => { version = neueVersion; },
    }).then((ok) => (ok ? version ?? frisch.version : null));
  }

  /** Der DRITTE Speicherweg — die Zahlungsvereinbarung. Reihenfolge, Version und Sinn
   *  stehen an `speichereZahlplan` (vorgangsdetail.ts): Kopf → Parteien → Zahlplan, und
   *  die Version ist die aus der ANTWORT des Parteien-Weges. */
  function zahlplanSpeichern(
    vorgangId: string, version: number, folgefehler: { e: unknown },
  ): Promise<boolean> {
    return speichereZahlplan({
      client, bezug: { art: "offer", customerId: angebot.customerId, vorgangId },
      version, griff: zahlplan,
      beiFehler: (meldung, fehler) => {
        folgefehler.e = folgewegFehler(meldung, fehler);
        zeigeAngebotsFehlerFeld(kopf, BEARBEITEN_PRAEFIX, fehler);
      },
    });
  }

  async function speichern(): Promise<void> {
    const problem = pruefeAngebot(entwurf, false);
    if (problem !== null) {
      setPruefung(problem);
      // Der Befund betrifft immer ein Feld im Kopfblock — der beim Bearbeiten zugeklappt
      // beginnt. Ohne dieses Aufklappen stünde die Meldung über einem Formular, in dem das
      // gemeinte Feld gar nicht zu sehen ist: ein unsichtbarer Fehler.
      zeigeAngebotsFeld(kopf, BEARBEITEN_PRAEFIX, problem.feld);
      return;
    }
    setPruefung(null);
    weg.current = kopfSpeichern;
    if (await formular.aktion() !== true) return;
    p.onSchliessen();
    await p.onNeuLaden();
  }

  /**
   * Der Speicherweg im Formularzustand: Kopf → Parteien → Zahlplan. Jeder Weg dreht die
   * Belegversion weiter; der nächste bekommt die aus der ANTWORT des vorigen — der Zahlplan
   * steht zuletzt, weil er der einzige Weg ist, der die geplanten Fälligkeiten neu rechnet.
   *
   * version = die gelesene Version: ein zwischenzeitlich fremd geänderter Satz ergibt 409
   * statt einer stillen Überschreibung. BINDEND FÜR VERWENDER: Die Liste darf nicht neu
   * laden, solange ein Detail in Bearbeitung ist — die Version, die hier hinausgeht, ist die
   * aus der Liste des Rahmens (`angebot`); ein Neuladen während der Bearbeitung ersetzte sie
   * unbemerkt durch die fremde und machte die optimistische Sperre wirkungslos.
   */
  async function kopfSpeichern(): Promise<boolean> {
    let frisch: Angebot;
    try {
      frisch = await client.updateOffer(
        kundeId, angebot.id, angebot.version, angebotKopffelder(entwurf));
    } catch (e) {
      // Die zweite Quelle von Feldbefunden: Der Server prüft mehr als die Maske
      // (Titel-Höchstlänge, Betragsgrenzen, Halbe-Kündigungsfrist, BETRAG_BERECHNET …).
      // Steht das Feld im zugeklappten Kopfblock, geht er im SELBEN Zug auf, in dem der
      // Befund ans Feld kommt — erst dann findet der Sprung zum ersten Fehler das Feld.
      if (betrifftKopfblock(ANGEBOT_KOPFFELDER, e)) kopf.oeffnen();
      throw e;
    }
    const folgefehler: { e: unknown } = { e: null };
    const parteienVersion = await parteienSpeichern(frisch, folgefehler);
    if (parteienVersion === null) throw folgefehler.e;
    if (!await zahlplanSpeichern(angebot.id, parteienVersion, folgefehler)) {
      throw folgefehler.e;
    }
    return true;
  }

  async function statusSetzen(status: OfferStatus): Promise<void> {
    setBeschaeftigt(true);
    p.onHinweis(null);
    // Wie an `alsPdf`/`inAkteAblegen`: eine ALTE PDF-Quittung ("wurde heruntergeladen …")
    // gehört zu einem vorigen Klick und nicht zu diesem Statuswechsel — sie bliebe sonst
    // stehen, bis (falls überhaupt) eine neue Meldung sie ersetzt (M6, Schlussrunde).
    setPdfMeldung(null);
    try {
      const neu = await client.setOfferStatus(kundeId, angebot.id, angebot.version, status);
      // Die Ablage-Auskunft des Statuswechsels (Spec Belegvorschau): Beim Versand legt
      // der Server das endgültige PDF in die Kundenakte. Gelingt es, steht der Stand
      // hier; scheitert es, ist der Status TROTZDEM gesetzt, und `ablageHinweis` sagt,
      // wie es nachzuholen ist. Beides wird in DIESELBE Meldungsstelle geschrieben wie
      // die PDF-Wege — eine zweite Zeile für dieselbe Sorte Auskunft wäre eine Stelle,
      // die man beim nächsten Fix vergisst.
      //
      // `neu.ablage === null` ist zweideutig (M1, Schlussrunde): An ACCEPTED/WITHDRAWN
      // versucht der Server gar nicht erst abzulegen (`ablageNachVersand` legt NUR bei
      // SENT ab) — dort heisst `null` "nichts versucht", nicht "nichts da", und eine
      // schon geladene Ablage darf stehen bleiben. Nur am WIRKLICHEN Versand (`status
      // === "SENT"`) ist `null` eine echte Auskunft ("versucht, nichts da") und wird
      // übernommen.
      if (neu.ablage !== null || status === "SENT") setAblage(neu.ablage);
      if (neu.ablageHinweis !== null) {
        setPdfMeldung({ text: neu.ablageHinweis, art: "schlecht" });
      } else if (neu.ablage !== null) {
        setPdfMeldung({
          text: `Das PDF liegt als „${neu.ablage.fileName}" in der Kundenakte.`,
          art: "gut",
        });
      }
      await p.onNeuLaden();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        p.onHinweis("Dieses Angebot wurde zwischenzeitlich geändert. Der aktuelle Stand ist "
          + "neu geladen.");
        await p.onNeuLaden();
      } else {
        p.onHinweis(apiMeldung(e, "Der Status konnte nicht geändert werden."));
      }
    } finally {
      setBeschaeftigt(false);
    }
  }

  /**
   * „PDF in Akte ablegen" — das Nachholen, wenn die Automatik beim Versand nicht kam
   * (oder das Erneuern nach einer Änderung).
   *
   * IDEMPOTENT: Liegt die Datei schon, wird ihr Inhalt ersetzt — dieselbe Kennung, kein
   * zweiter Eintrag, und ein vom Bearbeiter geänderter Name bleibt stehen.
   */
  async function inAkteAblegen(): Promise<void> {
    setBeschaeftigt(true);
    setPdfMeldung(null);
    try {
      const stand = await client.createOfferAblage(kundeId, angebot.id);
      setAblage(stand);
      // Derselbe Wortlaut wie am „PDF in Akte ablegen"-Knopf der Rechnung
      // (`RechnungDetail.inAkteAblegen`) — beide Belegarten sprechen hier bewusst dieselbe
      // Sprache. Die flüchtige Meldung und die DAUERHAFTE Ablage-Zeile unter den Aktionen
      // sagen zwar dasselbe, aber mit unterschiedlichem Wortlaut ("liegt JETZT" vs.
      // "ABGELEGT am") — genau deshalb lässt sich in Tests jede der beiden gezielt
      // ansprechen, ohne dass eine Suche nach dem Dateinamen beide träfe.
      setPdfMeldung({
        text: `Das PDF liegt jetzt in der Akte (${stand.fileName}) — im Reiter „Dateien".`,
        art: "gut",
      });
    } catch (e) {
      if (e instanceof ApiError && e.status === 501) {
        setPdfMeldung({
          text: "Die Render-Engine ist noch nicht angebunden — es entsteht noch kein PDF.",
          art: "hinweis",
        });
      } else {
        setPdfMeldung({
          text: apiMeldung(e, "Das PDF konnte nicht in der Kundenakte abgelegt werden."),
          art: "schlecht",
          ...(e instanceof ApiError && e.fields !== undefined ? { felder: e.fields } : {}),
        });
      }
    } finally {
      setBeschaeftigt(false);
    }
  }

  /**
   * „Als PDF" (E3-T4): Die Engine setzt das Angebot (Dokumentart ANGEBOT), die API
   * liefert den Download durch, hier wird er gespeichert. Die drei Ausgänge tragen ihre
   * ART mit (PDF_MELDUNGS_STIL): Erfolg als Status, die 501 der unkonfigurierten
   * Umgebung als HINWEIS (bekannter Arbeitsstand, kein Alarm), alles andere als Alarm.
   */
  async function alsPdf(): Promise<void> {
    setBeschaeftigt(true);
    setPdfMeldung(null);
    try {
      const { blob, filename } = await client.renderOffer(kundeId, angebot.id);
      const name = filename ?? "angebot.pdf";
      speichereBlob(blob, name);
      setPdfMeldung({ text: `Das PDF wurde heruntergeladen (${name}).`, art: "gut" });
    } catch (e) {
      if (e instanceof ApiError && e.status === 501) {
        setPdfMeldung({
          text: "Die Render-Engine ist noch nicht angebunden — es entsteht noch kein PDF.",
          art: "hinweis",
        });
      } else {
        // Nennt die Engine Feldpfade, reisen sie strukturiert mit (`fields[]`, an JEDEM
        // Engine-Fehlschlag) und werden unten als Liste gezeigt. Der Schlüssel entfällt,
        // wenn keine kamen — `exactOptionalPropertyTypes`.
        setPdfMeldung({
          text: apiMeldung(e, "Das Angebot konnte nicht als PDF ausgegeben werden."),
          art: "schlecht",
          ...(e instanceof ApiError && e.fields !== undefined ? { felder: e.fields } : {}),
        });
      }
    } finally {
      setBeschaeftigt(false);
    }
  }

  const { bestaetigen, bestaetigung } = useBestaetigung();

  /**
   * „Neue Version": eine Rückfrage, dann der Aufruf — und dann meldet das Detail dem
   * RAHMEN die NEUE Kennung (`onErsetzt`), statt sie hier weiterzuführen oder den Rahmen
   * bloss zum Neuladen zu bitten.
   *
   * Review-Befund Fixrunde 1: Ein `onNeuLaden` allein liefe mit der ALTEN Kennung —
   * `onNeuLaden` liest genau den Vorgang, den der Rahmen gerade führt, und das ist noch
   * der abgelöste. Es läse also die jetzt SUPERSEDED Fassung zurück und überschriebe die
   * eben angelegte neue Version sofort wieder. `onErsetzt` gibt dem Rahmen deshalb den
   * NEUEN Satz direkt mit — er muss ihn nicht ein zweites Mal vom Server holen — und mit
   * ihm auch dessen Kennung, damit der Rahmen seinen Bezug (und, wo es eine Adresse gibt,
   * sie selbst) umstellt und seine Liste nachzieht.
   */
  async function neueVersion(vorlageOfferId: string | null = null): Promise<void> {
    if (!(await bestaetigen(vorlageOfferId === null
      ? { titel: "Neue Version anlegen?", text: FRAGE_NEUE_VERSION,
          bestaetigenText: "Neue Version" }
      : { titel: "Fassung wiederherstellen?", text: FRAGE_WIEDERHERSTELLEN,
          bestaetigenText: "Wiederherstellen" }))) return;
    setBeschaeftigt(true);
    p.onHinweis(null);
    try {
      const neu = await client.neueOfferVersion(kundeId, angebot.id, vorlageOfferId);
      p.onHinweis(`Version ${neu.versionNumber} wurde angelegt. Die bisherige Fassung `
        + "ist jetzt veraltet.");
      p.onErsetzt(neu);
    } catch (e) {
      p.onHinweis(apiMeldung(e, "Die neue Version konnte nicht angelegt werden."));
    } finally {
      setBeschaeftigt(false);
    }
  }

  /**
   * „Als PDF" AN EINER BESTIMMTEN VERSION — der Weg aus dem Versionsabschnitt. Er ruft
   * denselben Rendern-Weg wie `alsPdf`, nur mit der Kennung der gewählten Version, und
   * meldet in dieselbe Rückmeldung (`pdfMeldung`). Eine zweite Meldungsstelle je Zeile
   * wäre ein zweiter Ort für dieselbe Auskunft.
   */
  async function alsPdfVon(v: AngebotVersion): Promise<void> {
    setBeschaeftigt(true);
    setPdfMeldung(null);
    try {
      const { blob, filename } = await client.renderOffer(kundeId, v.id);
      const name = filename ?? `angebot-v${String(v.versionNumber)}.pdf`;
      speichereBlob(blob, name);
      setPdfMeldung({ text: `Das PDF wurde heruntergeladen (${name}).`, art: "gut" });
    } catch (e) {
      setPdfMeldung({
        text: apiMeldung(e, "Diese Version konnte nicht als PDF ausgegeben werden."),
        art: e instanceof ApiError && e.status === 501 ? "hinweis" : "schlecht",
      });
    } finally {
      setBeschaeftigt(false);
    }
  }

  /** „Kopieren": dieselbe Bauart, anderes Ergebnis — ein Angebot mit EIGENER Nummer und
   *  eigener Kennung. Die Meldung nennt sie, weil genau sie der Unterschied zur neuen
   *  Version ist. Wie bei `neueVersion` meldet das Detail dem Rahmen die neue Kennung
   *  über `onErsetzt` — ohne das bliebe die Kopie unsichtbar: Sie steht unter einer
   *  ANDEREN Kennung als der gerade gezeigte Vorgang, und ein blosses „bitte neu laden"
   *  läse weiterhin nur diesen. */
  async function kopieren(): Promise<void> {
    if (!(await bestaetigen({
      titel: "Angebot kopieren?", text: FRAGE_KOPIEREN, bestaetigenText: "Kopieren",
    }))) return;
    setBeschaeftigt(true);
    p.onHinweis(null);
    try {
      const kopie = await client.kopiereOffer(kundeId, angebot.id);
      p.onHinweis(`Das Angebot wurde als ${kopie.offerNumber} kopiert.`);
      p.onErsetzt(kopie);
    } catch (e) {
      p.onHinweis(apiMeldung(e, "Das Angebot konnte nicht kopiert werden."));
    } finally {
      setBeschaeftigt(false);
    }
  }

  /**
   * Trägt das Panel ungespeicherte Eingaben? Der Kopfblock PARTEIEN zählt mit: Er meldet
   * seinen Entwurf erst, sobald ihn jemand angefasst hat — und genau dann gibt es etwas zu
   * verlieren.
   */
  const ungespeichert = bearbeitung
    && (!entwuerfeGleich(entwurf, angebotEntwurfAus(angebot))
      || parteien.beruehrt || zahlplan.beruehrt);

  /* Der Wächter gegen das Schliessen des BROWSERS (Global Constraint „kein stiller
     Datenverlust"): Der Abbrechen-Wächter fängt nur den Weg über den Knopf und Escape;
     ein geschlossener Reiter ginge an ihm vorbei. */
  useBeforeUnload(ungespeichert);

  const folgestatus = erlaubteFolgestatus(angebot.status);
  const kuerzel = companies.find((c) => c.id === angebot.companyId)?.shortCode ?? null;

  /**
   * Der EINE Ausgang des Bearbeitungspanels neben dem Speichern (Hausregel „striktes
   * Modalpanel", 06.09.2026): kein Ausgang unter laufendem Schreiben, Rückfrage bei
   * ungespeicherten Kopfeingaben. Knopf und Escape gehen durch diese Funktion.
   *
   * Verglichen wird gegen den Vorgang, wie er GERADE steht — nicht gegen einen zweiten,
   * mitgeführten Ausgangsstand: Ein Schritt im Positions-Editor dreht die Version weiter und
   * schreibt den Betrag ins Feld zurück (`positionenGeaendert`), und der Vorgang kommt über
   * `onFortschreiben` frisch wieder herein. Ein eingefrorener Ausgangsstand meldete danach
   * eine „Änderung", die längst gespeichert ist.
   *
   * Die POSITIONEN selbst stehen nie ungespeichert im Panel — der Editor schreibt jeden
   * Schritt sofort. Die Rückfrage kann sie deshalb übergehen, ohne etwas zu verschweigen.
   */
  async function abbrechen(): Promise<void> {
    if (speichert) return;
    if (!(await darfAbbrechen(ungespeichert))) return;
    parteien.zuruecksetzen();
    zahlplan.zuruecksetzen();
    formular.zuruecksetzen();
    p.onSchliessen();
  }

  /** Der Text der Hinweiszeile „fremder Stand“ (Slot `hinweis` des Formulars): wer wann
   *  geändert hat, sofern der Server es nennt, und was „Neu laden“ tut — die Eingaben
   *  stehen bis dahin lesbar da (nichts geht still verloren). */
  function fremderStandHinweis(): ReactNode {
    if (formular.zustand !== "fremder-stand") return undefined;
    const wer = formular.fremderStand?.changedBy;
    return (
      <>
        {wer !== undefined && wer !== ""
          ? `${wer} hat dieses Angebot inzwischen geändert.`
          : "Dieses Angebot wurde inzwischen geändert."}
        {" Deine Eingaben stehen noch da — „Neu laden“ holt den aktuellen Stand und "
          + "ersetzt sie."}
      </>
    );
  }

  /** Befunde je Feld: die des Servers und die der Maskenprüfung. */
  const fehlerJeFeld: Record<string, string> = {
    ...formular.feldFehler,
    ...(pruefung !== null ? { [pruefung.feld]: pruefung.meldung } : {}),
    // Die Befunde der beiden Folgewege stehen in IHREN Blöcken — für den Fehlerzähler
    // des Kopfblocks zählen sie mit (beide stehen darin).
    ...parteien.befunde, ...zahlplan.befunde,
  };

  return (
    <>
      <div className="kang__zeile">
        <span className="kang__nummer">{angebot.offerNumber}</span>
        {/* Die Versionspille steht NEBEN der Nummer, nicht IN ihr (Festlegung V4):
            Die Nummer bleibt die Nummer, auch beim Kopieren in die Zwischenablage. */}
        {versionsZusatz(angebot.versionNumber) !== "" && (
          <span className="kang__version">{versionsZusatz(angebot.versionNumber)}</span>
        )}
        {kuerzel !== null && <span className="kang__kuerzel">{kuerzel}</span>}
        <span className="kang__eintrag-titel">{angebot.title}</span>
        <span className={`kang__status kang__status--${angebot.status.toLowerCase()}`}>
          {ANGEBOT_STATUS_LABEL[angebot.status]}
        </span>
      </div>

      {/* Die abgelöste Version sagt es SELBST und nennt, wodurch: Ohne diesen Satz
          stünde ein Bearbeiter vor einem Angebot ohne Bearbeiten-Knopf und wüsste
          nicht, warum. Die Ordnungszahl kommt aus dem geladenen Versionsabschnitt
          (`versionen[0]` — jüngste zuerst): `angebot.versionCount` ist der Stand BEIM
          LESEN dieser (abgelösten) Zeile und kann inzwischen veraltet sein, wenn seither
          eine weitere Version entstand. Solange die Liste noch lädt, bleibt `versionCount`
          der einzig verfügbare Rückfall (Fixrunde nach Gesamtreview, M1). */}
      {!angebot.istJuengste && (
        <Hinweis art="warnung">
          Veraltet, ersetzt durch {versionsZusatz(versionen?.[0]?.versionNumber
            ?? angebot.versionCount)}
        </Hinweis>
      )}

      <div className="kang__daten">
        <span className="kang__betrag ki-num">
          {formatBetrag(angebot.netAmount, angebot.currency)}
        </span>
        {angebot.validUntil !== null && (
          <span className="kang__gueltig">
            gültig bis {formatDatum(angebot.validUntil)}
          </span>
        )}
        {angebot.contactName !== null && angebot.contactName !== "" && (
          <span className="kang__kontakt">{angebot.contactName}</span>
        )}
      </div>

      {/* Die PDF-Rückmeldung steht AM VORGANG, zu dem sie gehört: Der Baustein trägt sie
          samt ihrer Befundliste selbst, damit sie auch dort erscheint, wo kein Reiter
          darüber liegt (der Angebots-Bereich aus Aufgabe 3). */}
      {pdfMeldung !== null && (
        <Hinweis art={PDF_HINWEIS_ART[pdfMeldung.art]}>{pdfMeldung.text}</Hinweis>
      )}
      {/* Die Feldbefunde als LISTE — jeder Pfad einzeln und ungekürzt, zum Abhaken.
          NEBEN der Meldung und nicht in ihr: Die trägt `role="alert"` und muss ein `<p>`
          bleiben (ein `<ul>` darf dort gar nicht hinein), und die Ansage soll ein SATZ
          sein, keine vorgelesene Liste.

          EIGENER Blockname `kang__` und kein geteilter Block mit den Rechnungen: Dieselbe
          Klasse über zwei Ansichten hinweg hiesse, dass eine Stilanpassung an der einen
          die andere still mitverändert. Die Gestalt ist bewusst dieselbe, der Name nicht. */}
      {pdfMeldung?.felder !== undefined && pdfMeldung.felder.length > 0 && (
        <ul className="kang__fehlerfelder" aria-label="Fehlende Pflichtwerte">
          {pdfMeldung.felder.map((f) => (
            <li key={f.field}>
              <code>{f.field}</code>: {f.message}
            </li>
          ))}
        </ul>
      )}

      {bearbeitung && (
        <Modal titel={`Angebot ${angebot.offerNumber} bearbeiten`} breite="weit"
          onAbbrechen={abbrechen} abbrechenGesperrt={speichert}>
          <div className="kang__bearbeitung">
            {angebotFormularFelder({
              praefix: BEARBEITEN_PRAEFIX, mitUnternehmen: false, companies, umfeld,
              entwurf, setEntwurf, hatPositionen, kopf, fehlerJeFeld,
              // Im Subdialog „Vorlage anwenden“ steht SEIN Fehlschlag dort — nicht doppelt.
              feldFehler: pruefung?.meldung
                ?? (anwendenWahl !== null && anwendenVersucht ? null : formular.meldung),
              formular: { ...formular.formularProps, hinweis: fremderStandHinweis() },
              vorschauWerte: angebotVorschauWerte({
                entwurf, companies, kontakte: umfeld.kontakte, angebot,
                kundeName: p.kundeName,
              }),
              /* Der Kopfblock PARTEIEN — `key` am Vorgang: Ein Wechsel des bearbeiteten
                 Angebots baut ihn neu auf, statt den Entwurf des vorigen weiterzureichen.
                 Nur beim BEARBEITEN, nicht beim Anlegen: Vor dem ersten Speichern gibt es
                 keinen Beleg, dessen Parteien man setzen könnte. */
              parteien: (
                <BelegParteien key={`${angebot.id}-${String(standNeuLaden)}`}
                  client={client} art="offer"
                  customerId={angebot.customerId} vorgangId={angebot.id}
                  idPraefix={BEARBEITEN_PRAEFIX} verdeckt={formular.zustand !== "bereit"}
                  befunde={parteien.befunde} onEntwurf={parteien.melde} />
              ),
              /* Der Abschnitt ZAHLUNGSVEREINBARUNG — `key` am Vorgang aus demselben Grund
                 wie an PARTEIEN: „Neue Version"/„Kopieren" wechseln die Kennung, und ein
                 Wechsel muss den Entwurf des VORIGEN Belegs verwerfen, statt seine (jetzt
                 fremden) Ratenkennungen weiterzutragen. */
              zahlplan: (
                <Zahlplan key={`${angebot.id}-zahlplan-${String(standNeuLaden)}`}
                  client={client}
                  bezug={{ art: "offer", customerId: angebot.customerId,
                           vorgangId: angebot.id }}
                  idPraefix={BEARBEITEN_PRAEFIX} verdeckt={formular.zustand !== "bereit"}
                  befunde={zahlplan.befunde} onEntwurf={zahlplan.melde} />
              ),
              /* Der Kasten „Anhänge" — `key` am Vorgang aus demselben Grund wie an
                 PARTEIEN/ZAHLPLAN. DIESELBE Naht wie beim Positions-Editor: Jeder
                 Schreibweg des Kastens dreht die Belegversion weiter, und das nächste
                 „Speichern" muss sie kennen — sonst schickt es eine veraltete Version und
                 bekommt eine 409 für eine Änderung, die niemand gemacht hat. */
              anhaenge: (
                <Anhaenge key={`${angebot.id}-anhaenge-${String(standNeuLaden)}`}
                  client={client}
                  bezug={{ art: "offer", customerId: angebot.customerId,
                           vorgangId: angebot.id }}
                  idPraefix={BEARBEITEN_PRAEFIX} verdeckt={formular.zustand !== "bereit"}
                  // Die AKTUELLE Belegversion geht HEREIN (Review-Befund zur Fixrunde
                  // K10, 24.09.2026): Positionen und Kopf-Speichern drehen `angebot.version`
                  // im selben Dialog weiter, ohne dass der Kasten selbst neu liest — ohne
                  // diese Eigenschaft liefe sein NÄCHSTER eigener Schreibweg (Ziehen,
                  // Entfernen, Anhängen) mit einer veralteten Version in eine 409.
                  version={angebot.version}
                  onVersionChange={(version) => {
                    p.onFortschreiben({ ...angebot, version });
                  }} />
              ),
              /* Der grösste Block, in der Mitte der Maske (Entscheid Dirk 08.09.2026):
                 zwischen Einleitungs- und Ausleitungstext, in voller Breite. Die Positionen
                 hängen an DEMSELBEN Vorgang wie die Kopffelder darüber — jeder Schritt im
                 Editor dreht die Version des Angebots weiter, und `positionenGeaendert`
                 trägt sie über den Rahmen zurück, sonst liefe das nächste Speichern des
                 Kopfes in eine 409. */
              positionen: (
                <div className="kang__positionen">
                  <div className="kang__positionen-kopf">
                    <h3 className="kang__abschnitt">Positionen</h3>
                    <button type="button" className="kang__sekundaer" ref={anwendenKnopf}
                      disabled={speichert || formular.zustand === "fremder-stand"}
                      onClick={() => { setAnwendenVersucht(false); setAnwendenWahl(""); }}>
                      Vorlage anwenden
                    </button>
                  </div>

                  {/* Der Anwenden-Dialog sagt VORHER, was geschieht: Die Positionen kommen
                      ANS ENDE (nichts wird ersetzt), und die Texte übernimmt der Server nur,
                      wenn sie leer sind. Ohne diesen Satz wäre „anwenden" eine Wette darauf,
                      ob die eigene Arbeit stehen bleibt.

                      Seit ME-Etappe 3 (Aufgabe 4) ein SUBDIALOG über der Maske (me-dialog
                      `unter="seitlich"`, die Hülle wählt `stapel`, wo der Platz fehlt) statt
                      eines Kastens im Formular: Er hat eigene Ausgänge (Anwenden/Abbrechen),
                      und Escape schliesst NUR ihn — die Maske darunter behält ihren
                      Entwurf (Review-Fokus 4). */}
                  {anwendenWahl !== null && (
                    <Modal titel="Vorlage anwenden" onAbbrechen={() => { void anwendenAbbrechen(); }}
                      abbrechenGesperrt={speichert}>
                      <p className="kang__vorlagen-hinweis">
                        Die Positionen der Vorlage werden an die bestehende Liste angehängt;
                        Einleitungs- und Ausleitungstext werden nur übernommen, wenn sie leer
                        sind. Periodenregel und Zahlplan bleiben unverändert — sie kommen nur
                        mit, wenn das Angebot MIT der Vorlage angelegt wird. Noch nicht
                        gespeicherte Textänderungen ersetzt dabei der Stand vom Server.
                      </p>
                      {anwendenVersucht && formular.meldung !== null && (
                        <Hinweis art="fehler">{formular.meldung}</Hinweis>
                      )}
                      <Formular beschriftung="oben" laeuft={speichert}>
                        <Auswahl id="kang-anwenden-vorlage" name="kang-anwenden-vorlage"
                          beschriftung="Vorlage" wert={anwendenWahl}
                          onGeaendert={(d) => setAnwendenWahl(d.wert)}>
                          <option value="">Bitte wählen …</option>
                          {umfeld.vorlagen.map((v) => (
                            <option key={v.id} value={v.id}>
                              {v.name} ({v.itemCount}{" "}
                              {v.itemCount === 1 ? "Position" : "Positionen"})
                            </option>
                          ))}
                        </Auswahl>
                      </Formular>
                      <ModalFuss speichernText="Anwenden"
                        speichernGesperrt={speichert || anwendenWahl === ""}
                        abbrechenGesperrt={speichert}
                        onSpeichern={() => { void vorlageAnwenden(anwendenWahl); }}
                        onAbbrechen={() => { void anwendenAbbrechen(); }} />
                    </Modal>
                  )}

                  <PositionsEditor
                    // Der Zähler im Schlüssel hängt den Editor nach einem Anwenden neu auf —
                    // nur so lädt er die angehängten Zeilen (er führt seinen Stand selbst).
                    key={`${angebot.id}-${String(positionenNeuLaden)}`}
                    client={client}
                    bezug={{ art: "offer", customerId: kundeId, vorgangId: angebot.id }}
                    companyId={angebot.companyId} version={angebot.version}
                    gesperrt={formular.zustand !== "bereit"}
                    onVersionChange={positionenGeaendert} />
                </div>
              ),
            })}
          </div>

          {/* Die zwei einzigen Ausgänge, unten am Panel: Speichern schreibt die KOPFFELDER
              (die Positionen schreibt der Editor Schritt für Schritt selbst). */}
          <ModalFuss speichernText="Speichern"
            speichernGesperrt={formular.zustand !== "bereit"}
            abbrechenGesperrt={speichert}
            onSpeichern={() => { void speichern(); }} onAbbrechen={abbrechen} />
        </Modal>
      )}

      {!bearbeitung && versionen !== null && versionen.length > 1 && (
        <section className="kang__versionen" aria-label="Versionen">
          <h3 className="kang__abschnitt">Versionen</h3>
          {/* me-tabelle (ME-Etappe 3, Aufgabe 4): Rahmen, Kopfzeile, Zebra und das
              waagerechte Rollen bringt das Element; der Betrag steht rechtsbündig. */}
          <Tabelle className="kang__versionen-tabelle">
          <table>
            <thead>
              <tr>
                <th scope="col">Version</th>
                <th scope="col">Datum</th>
                <th scope="col">Status</th>
                <th scope="col" data-ausrichtung="rechts">Betrag</th>
                <th scope="col">Aktionen</th>
              </tr>
            </thead>
            {/* JÜNGSTE OBEN (Hausregel „Wichtigstes oben") — der Server liefert sie
                bereits in dieser Reihenfolge; hier wird NICHT nachsortiert. */}
            <tbody>
              {versionen.map((v) => (
                <tr key={v.id}
                  className={v.id === angebot.id ? "kang__versionen-zeile--aktiv" : undefined}
                  aria-current={v.id === angebot.id ? "true" : undefined}>
                  <th scope="row">{versionsZusatz(v.versionNumber) || "v1"}</th>
                  <td>{formatDatum(v.createdOn)}</td>
                  <td>{ANGEBOT_STATUS_LABEL[v.status]}</td>
                  <td className="ki-num" data-ausrichtung="rechts">
                    {formatBetrag(v.netAmount, angebot.currency)}
                  </td>
                  <td className="kang__versionen-aktionen">
                    {/* JEDE Version bleibt als PDF reproduzierbar — das ist der Kern
                        der Spec („Jede Version ist vollständig lesbar und als PDF
                        reproduzierbar, auch nachdem eine neuere entstanden ist"). Der
                        Rendern-Weg nimmt die Kennung DIESER Version, nicht die des
                        gezeigten Angebots. */}
                    <button type="button" className="kang__sekundaer"
                      aria-label={`Version ${v.versionNumber} als PDF`}
                      disabled={gesperrt || beschaeftigt}
                      onClick={() => { void alsPdfVon(v); }}>
                      Als PDF
                    </button>
                    {/* „Version wiederherstellen" ist keine eigene Mechanik, sondern
                        eine neue Version MIT DIESER als Vorlage — deshalb steht der
                        Knopf hier und heisst, was er tut. An der gezeigten (jüngsten)
                        Version selbst wäre er der gewöhnliche „Neue Version"-Knopf und
                        deshalb weggelassen. */}
                    {angebot.darfNeueVersion && v.id !== angebot.id && (
                      <button type="button" className="kang__sekundaer"
                        aria-label={`Version ${v.versionNumber} als Vorlage für neue Version`}
                        disabled={gesperrt || beschaeftigt}
                        onClick={() => { void neueVersion(v.id); }}>
                        Als Vorlage für neue Version
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </Tabelle>
        </section>
      )}

      {/* Der Abschnitt ZAHLUNGSVEREINBARUNG im geschlossenen Detail — LESEND, die
          fertigen Sätze des Servers: Gepflegt wird der Zahlplan im Modal, hier steht nur
          das Ergebnis. Eigener Wrapper `kang__zahlplan` allein für den Abstand an diesem
          Einbauort (Kollisions-Lehre 01.09.: kein geteilter Blockname für `zpl`).
          HINTER EINEM AUFKLAPPER, geschlossen (Gesamtreview, I2): `Zahlplan` ruft in
          seinem Effekt sofort `getZahlplan` ab, sobald er gezeichnet wird — bei vielen
          Angeboten in der Liste wäre das ein Abruf je Zeile, den fast niemand ansieht.
          Erst das Öffnen hängt ihn ein. */}
      {!bearbeitung && (
        <div className="kang__zahlplan">
          <Aufklapper faltung={zahlplanFaltung} titel="Zahlungsvereinbarung"
            inhaltId="kang-zahlplan-inhalt">
            {zahlplanFaltung.offen && (
              <Zahlplan client={client}
                bezug={{ art: "offer", customerId: kundeId, vorgangId: angebot.id }}
                idPraefix="kang-lese" nurLesend />
            )}
          </Aufklapper>
        </div>
      )}

      {/* Der Kasten „Anhänge" im geschlossenen Detail — LESEND, hinter EINEM eigenen
          Aufklapper (Kollisions-Lehre 01.09.: kein geteilter Blockname; eigener Wrapper
          `kang__anhaenge` allein für den Abstand an diesem Einbauort). GESCHLOSSEN und
          LAZY, aus demselben Grund wie beim Zahlplan (Gesamtreview, I2): `Anhaenge` holt
          in seinem Effekt sofort `getAnhaenge` ab, sobald es gezeichnet wird — bei vielen
          Angeboten in der Liste wäre das ein Abruf je Zeile, den fast niemand ansieht. */}
      {!bearbeitung && (
        <div className="kang__anhaenge">
          <Aufklapper faltung={anhaengeFaltung} titel="Anhänge"
            inhaltId="kang-anhaenge-inhalt">
            {anhaengeFaltung.offen && (
              <Anhaenge client={client}
                bezug={{ art: "offer", customerId: kundeId, vorgangId: angebot.id }}
                idPraefix="kang-lese" nurLesend />
            )}
          </Aufklapper>
        </div>
      )}

      {!bearbeitung && (
        <div className="kang__aktionen">
          {/* Bearbeiten NUR an der jüngsten Version. Eine ältere ist lesend — der
              Server wiese sie mit 422 ANGEBOT_VERALTET ab, und ein Knopf, der das
              sicher auslöst, ist keine Bedienführung, sondern eine Falle. */}
          {angebot.istJuengste && (
            <button type="button" className="kang__sekundaer"
              aria-label={`${angebot.title} bearbeiten`}
              disabled={gesperrt || beschaeftigt}
              onClick={p.onBearbeiten}>
              Bearbeiten
            </button>
          )}
          {/* Die Vorschau — DIREKT NEBEN „Als PDF" (Spec, wörtlich): Beide zeigen dasselbe
              Druckbild, einmal auf dem Bildschirm, einmal als Datei. */}
          <button type="button" className="kang__sekundaer"
            disabled={gesperrt || beschaeftigt}
            onClick={() => setVorschauOffen(true)}>
            Vorschau
          </button>
          <button type="button" className="kang__sekundaer"
            disabled={gesperrt || beschaeftigt}
            onClick={() => { void alsPdf(); }}>
            Als PDF
          </button>
          {folgestatus.map((s) => (
            <button key={s} type="button" className="kang__sekundaer"
              disabled={gesperrt || beschaeftigt}
              onClick={() => { void statusSetzen(s); }}>
              {STATUS_AKTION[s]}
            </button>
          ))}
          {/* Der Satz steht NEBEN dem Knopf und nicht in ihm: Die Beschriftung soll
              kurz bleiben, die Ansage ausdrücklich sein. */}
          {folgestatus.includes("SENT") && (
            <span className="kang__statushinweis">{SENT_HINWEIS}</span>
          )}
          {/* Die Ablage in der Kundenakte NACHHOLEN oder ERNEUERN — sichtbar, sobald wir
              eine Antwort auf „liegt sie?" haben (`ablage !== undefined`). Das ist bewusst
              NICHT an `angebot.status` gebunden: Direkt nach „Als gesendet markieren" hat
              der Rahmen die frische Version des Angebots (noch) nicht nachgeladen, der
              Ablagestand aus der STATUSANTWORT ist aber schon da — und genau dann soll der
              Knopf (bzw. die Auskunft, dass es NICHT geklappt hat) sofort stehen, nicht erst
              nach einem Neuladen. Am Entwurf UND an einem nie gesendeten Angebot bleibt
              `ablage` auf `undefined` (siehe `ablageLesen`), der Knopf fehlt also dort
              ganz — ein Klick würde der Server mit 422 ABLAGE_UNZULAESSIG abweisen, und
              das wäre keine Bedienführung, sondern eine Falle (dieselbe Regel wie am
              „Bearbeiten" einer alten Version). Die zusätzliche Prüfung auf
              `angebot.sentOn` (Schlussrunde I1) fängt den Wimpernschlag ab, in dem beim
              Wechsel des aufgeschlagenen Angebots der ALTE `ablage`-Stand noch steht,
              während `angebot` schon das NEUE (nie gesendete) zeigt. */}
          {ablage !== undefined && angebot.sentOn !== null && (
            <button type="button" className="kang__sekundaer"
              disabled={gesperrt || beschaeftigt}
              onClick={() => { void inAkteAblegen(); }}>
              {/* Wie an der Rechnung: „ablegen" ist das erste Mal, „erneuern" sagt, dass
                  schon etwas liegt und ersetzt wird (IDEMPOTENT, siehe `inAkteAblegen`). */}
              {ablage === null ? "PDF in Akte ablegen" : "PDF in der Akte erneuern"}
            </button>
          )}
          {angebot.darfNeueVersion && (
            <button type="button" className="kang__sekundaer"
              disabled={gesperrt || beschaeftigt}
              onClick={() => { void neueVersion(null); }}>
              Neue Version
            </button>
          )}
          {/* Kopieren geht IMMER — aus jedem Status, auch aus einer veralteten Version
              und auch nach abgeleitetem Auftrag (Spec). */}
          <button type="button" className="kang__sekundaer"
            disabled={gesperrt || beschaeftigt}
            onClick={() => { void kopieren(); }}>
            Kopieren
          </button>
          {angebot.status === "ACCEPTED" && (
            <button type="button" className="kang__auftrag"
              disabled={gesperrt}
              onClick={() => p.onAuftragAusAngebot(angebot)}>
              Auftrag anlegen
            </button>
          )}
        </div>
      )}

      {/* Der Ablagestand — unter den Aktionen, in gedämpfter Farbe (Randnotiz, kein
          Alarm). NUR am Angebot, das der REIHE NACH (also über die Eigenschaft `angebot`,
          nicht nur den lokalen Stand) den Entwurf verlassen hat UND wirklich gesendet
          wurde (`sentOn`, Schlussrunde I1 — sonst erschiene an einem direkt aus dem
          Entwurf zurückgezogenen Angebot fälschlich „Noch nicht in der Akte abgelegt").
          Direkt nach „Als gesendet markieren" führt der Rahmen noch die alte (DRAFT-)
          Fassung, bis er neu lädt — die FRISCHE Auskunft steht in diesem Moment in der
          Meldung oben (`pdfMeldung`, siehe `statusSetzen`), nicht hier. Zwei Stellen, die
          im selben Wimpernschlag dasselbe sagten, wären eine Text-Dopplung, an der man
          beim nächsten Fix nur eine ändert. */}
      {!bearbeitung && angebot.status !== "DRAFT" && angebot.sentOn !== null
        && ablage !== undefined && (
        ablage === null
          ? (
            <p className="kang__ablage" role="status">
              Noch nicht in der Akte abgelegt.
              {" Mit „PDF in Akte ablegen“ lässt sich das nachholen."}
            </p>
          )
          : (
            <p className="kang__ablage" role="status">
              {`Abgelegt in der Akte am ${formatDatum(ablage.filedAt)} als „${ablage.fileName}" `}
              {`— zu finden im Reiter „Dateien" dieses Kunden.`}
            </p>
          )
      )}

      {vorschauOffen && (
        <Belegvorschau
          // Über denselben Helfer wie die übrigen Versionsanzeigen (M3, Schlussrunde):
          // Massgeblich ist `versionNumber`, nicht `versionCount` — die ERSTE Version
          // einer mehrversionigen Reihe trägt kein „v1" (versionsZusatz ist leer bei 1).
          titel={`Angebot ${angebot.offerNumber}`
            + `${versionsZusatz(angebot.versionNumber) !== ""
              ? ` ${versionsZusatz(angebot.versionNumber)}` : ""}`
            + " · Vorschau"}
          // Die FASSUNG: Die Version wechselt bei jeder Änderung, und genau dann ist das
          // gezeigte Blatt nicht mehr das aktuelle (Ruling K6).
          fassung={String(angebot.version)}
          laden={() => client.vorschauOffer(kundeId, angebot.id)}
          // Der PDF-Knopf der Vorschau geht den BESTEHENDEN Download-Weg — kein zweiter
          // Weg zum selben Ziel (Spec, wörtlich: „der bestehende Download-Weg").
          aufPdf={() => { void alsPdf(); }}
          onSchliessen={() => setVorschauOffen(false)} />
      )}
      {bestaetigung}
    </>
  );
}
